import React, { useState, useEffect, useMemo, useCallback } from "react";
import { apiFetch, fmt, can, getUser } from "../api";
import { StatusPill, Spinner, EmptyState, SummaryRibbon, RibbonCell } from "../components/ui";
import { useNavigation } from "../components/nav/NavigationContext";
import { REPORTS, reportsByCategory, CATEGORY_ORDER, reportVisible } from "../reports/reportRegistry";
import { useReportNav } from "../reports/useReportNav";
import { useSavedViews } from "../reports/savedViewsApi";
import { SortableTh, sortRowsBy } from "../components/SortableTable";
import PaymentHistoryFilters, { DEFAULT_FILTERS as DEFAULT_PH_FILTERS, buildPaymentHistoryQuery } from "../components/PaymentHistoryFilters";
import ProductivityReport from "../components/ProductivityReport";
import ProductionByParty from "../components/ProductionByParty";
import BordereauReports from "../components/BordereauReports";
import ReimbursementRequestsReport from "../components/ReimbursementRequestsReport";

// ============================================================================
// Reports page (Chunk 7G refresh)
// ----------------------------------------------------------------------------
// Two report views (loss-run, payment-history) share one layout. Each has its
// own sort state because the column lists are different. Filter bar + stat
// cards live in .list-header; the table + sticky thead live in .list-body.
// ============================================================================

// Column labels untangled post INSURED-LIFT-1 (2026-05-11): "Insured" now
// means the policy-holding company (sourced from claim_parties role='Insured');
// "Claimant" is the injured crew member.  VCA's field-naming inversion is
// kept entirely on the data-side; user-facing labels are the real terms.
// RP-LOSS-RUN-POLISH v1 (2026-05-12, polished same day): column shape per
// Kuhrt's locked spec.
// Order: Claimant, Claim #, Vessel, Status, DOL, Total Reserve, Remaining
// Reserve, Total Payments, Deductible (disabled), Paid By Policy (disabled).
// Insured intentionally omitted as a column — rendered as the group header
// row in the grouped-by-Insured layout.
//
// LABELS: canonical UI-WORKBENCH-1.1 trio everywhere (column headers + stat
// strip + subtotal/grand-total rows + all surfaces): "Total Reserve" /
// "Total Payments" / "Remaining Reserve" (singular).  Kuhrt's polish review
// 2026-05-12 standardized on canonical labels — earlier session brief used
// plural variants ("Total Reserves" / "Total Paid"), now retired.
//
// Disabled-with-tooltip columns pending POLICY-DETAILS-COVERAGE-LIFT.
// vca_coverage has the data (74% claim coverage on CoverageDeductable, 100%
// CoverageType across 8 clean values) but the native policy/coverage model
// it lifts into hasn't been designed yet.  Header shows an ⓘ glyph as the
// disabled-affordance hint; full reason on hover via title attribute.
// POL-6 (2026-05-15) — Loss Run Deductible cell renderer.
// Row schema from backend (/reports/loss-run):
//   deductible_lines : count of claim_coverages rows with non-null per_claim_deductible
//   deductible_min   : min per_claim_deductible across those rows (or null)
//   deductible_max   : max per_claim_deductible across those rows (or null)
function renderDeductibleCell(r) {
  const count = Number(r.deductible_lines || 0);
  if (count === 0) {
    return <span title="No coverage data recorded for this claim."
                 style={{ color: "var(--text3)", fontStyle: "italic", cursor: "help" }}>—</span>;
  }
  const min = Number(r.deductible_min);
  const max = Number(r.deductible_max);
  if (count === 1 || min === max) {
    return fmt.money(min);
  }
  // Multi-coverage with non-uniform deductibles.  Show range + tooltip
  // pointing adjusters to the Policy & Coverage tab for the per-line view.
  return (
    <span title={`Claim has ${count} coverage lines with different deductibles. Open the claim's Policy & Coverage tab for the per-line view.`}
          style={{ color: "var(--amber, #B07000)", fontWeight: 600, cursor: "help" }}>
      {fmt.money(min)} – {fmt.money(max)} ⓘ
    </span>
  );
}

const LOSS_RUN_COLUMNS = [
  { key:"claimant_name",     label:"Claimant",            align:undefined },
  { key:"claim_number",      label:"Claim #",             align:undefined },
  { key:"vessel_name",       label:"Vessel",              align:undefined },
  { key:"claim_status",      label:"Status",              align:undefined },
  { key:"business_line",     label:"Line",                align:undefined },
  { key:"claim_type_label",  label:"Type",                align:undefined },
  { key:"date_of_loss",      label:"DOL",                 align:undefined },
  { key:"total_reserves",    label:"Total Reserve",       align:"right"   },
  { key:"remaining_reserves",label:"Remaining Reserve",   align:"right"   },
  { key:"total_paid",        label:"Total Payments",      align:"right"   },
  // POL-6 (2026-05-15): Deductible enabled from native claim_coverages.
  // Display rule: 0 lines → em-dash; 1 line OR min==max → single value;
  // otherwise "$min – $max" range with tooltip listing each coverage line.
  // Subtotal / Grand Total rows are intentionally blank (sum of ranges is
  // not meaningful — adjusters check per-claim).
  { key:"deductible",        label:"Deductible",          align:"right" },
  // REPORTING-PHASE-2 (2026-05-29): Retained / Paid-By-Policy split, derived.
  //   retained        = total paid up to the per-claim deductible cap (Insured's $).
  //   paid_by_policy  = total paid above the cap (carrier's exposure).
  { key:"retained",          label:"Retained",            align:"right" },
  { key:"paid_by_policy",    label:"Paid By Policy",      align:"right" },
];

// RP-LOSS-RUN-COLS (2026-06-18): optional descriptive columns, off by default,
// toggled by the user above the report. Inserted right after DOL so the money
// columns stay grouped at the right. Sources: loss_description on claims;
// cause_of_loss / location_on_vessel / body_parts on claimant_details (native
// materialized claimant — blank where not yet materialized). date_closed is
// already returned by the Loss Run SQL.
const LOSS_RUN_OPTIONAL = [
  { key:"date_closed",        label:"Closed",             render: r => r.date_closed ? fmt.date(r.date_closed) : "—" },
  { key:"loss_description",   label:"Loss Description",   render: r => r.loss_description || "—" },
  { key:"cause_of_loss",      label:"Cause of Loss",      render: r => r.cause_of_loss || "—" },
  { key:"location_on_vessel", label:"Location of Vessel", render: r => r.location_on_vessel || "—" },
  { key:"body_parts",         label:"Body Part",          render: r => (Array.isArray(r.body_parts) ? r.body_parts.join(", ") : r.body_parts) || "—" },
];

// REPORTING-PHASE-2 (2026-05-29): Reimbursement Status Report — cross-claim,
// grouped by Insured.  Mirrors the legacy KM "All Claims Reimbursement Status".
// Math: plans/REPORT_VOCABULARY_AND_MATH.md §4 universal formula card.
// Columns marked `money:true` are summed in the per-Insured subtotal +
// grand-total rows.
const REIMB_STATUS_COLUMNS = [
  { key:"claimant_name",        label:"Claimant",                 align:undefined },
  { key:"claim_number",         label:"Claim #",                  align:undefined },
  { key:"adjuster_name",        label:"Lead Adjuster",            align:undefined },
  { key:"date_of_loss",         label:"DOL",                      align:undefined },
  { key:"gross_reserves",       label:"Gross Reserves",           align:"right", money:true },
  { key:"total_paid",           label:"Total Exp. & Fees",        align:"right", money:true },
  { key:"paid_by_insured",      label:"Paid by Insured",          align:"right", money:true,
    title:"Expenses/Deposits Paid by Insured = Total Payments + Trust Account balance" },
  { key:"reimbursements",       label:"Reimbursements",           align:"right", money:true },
  { key:"deductible",           label:"Deductible",               align:"right", money:true },
  { key:"net_due",              label:"Net Due to Insured",       align:"right", money:true,
    title:"Paid by Insured − Reimbursements − Deductible (clamped at $0)" },
  { key:"pending_ir",           label:"Pending Requests",         align:"right", money:true },
  { key:"trust_balance",        label:"Trust Account",            align:"right", money:true,
    title:"Trust contributions − trust-funded payments" },
  { key:"aad",                  label:"AAD",                      align:"right", money:true,
    title:"Annual Aggregate Deductible (per-policy; blank treated as $0)" },
  { key:"net_due_less_pending", label:"Net Due Less Pending/AAD", align:"right", money:true,
    title:"Net Due − Pending − Trust − AAD (clamped at $0)" },
];

const PAYMENT_HIST_COLUMNS = [
  { key:"claim_number",  label:"Claim #",   align:undefined },
  { key:"insurer",       label:"Insurer",   align:undefined },
  { key:"invoice_number",label:"Invoice #", align:undefined },
  { key:"invoice_date",  label:"Date",      align:undefined },
  { key:"total_amount",  label:"Amount",    align:"right"   },
  { key:"approved_amount",label:"Approved", align:"right"   },
  { key:"payment_date",  label:"Pay Date",  align:undefined },
  { key:"payment_method",label:"Method",    align:undefined },
  { key:"check_number",  label:"Check #",   align:undefined },
  { key:"status",        label:"Status",    align:undefined },
];

// RP-1 (2026-05-07): user-scoped Open/Closed Claims report.  Trio uses the
// canonical UI-WORKBENCH-1.1 math; same column shape as the Workbench grid
// for visual consistency.
const OPEN_CLOSED_COLUMNS = [
  { key:"claim_number",       label:"Claim #",            align:undefined },
  { key:"claim_status",       label:"Status",             align:undefined },
  { key:"claim_type",         label:"Type",               align:undefined },
  { key:"insured_name",       label:"Insured",            align:undefined },
  { key:"claimant_name",      label:"Claimant",           align:undefined },
  { key:"date_of_loss",       label:"DOL",                align:undefined },
  { key:"lead_adjuster_name", label:"Adjuster",           align:undefined },
  { key:"vessel_name",        label:"Vessel",             align:undefined },
  { key:"total_reserves",     label:"Total Reserve",      align:"right"   },
  { key:"total_paid",         label:"Total Payments",     align:"right"   },
  { key:"remaining",          label:"Remaining Reserve",  align:"right"   },
  { key:"open_tasks",         label:"Tasks",              align:"center"  },
];

// RP-2 (2026-05-07): Adjuster WIP — actionable invoice-readiness work surface.
// Claim-level summary + expandable drilldown (time/expense line items).
// Two modes: "worked" (own WIP across worked claims) / "leading" (all WIP on
// claims you lead).  Action column hidden in worked mode (only the lead can
// trigger invoice prep on a claim).
const WIP_COLUMNS_BASE = [
  { key:"claim_number",          label:"Claim #",          align:undefined },
  { key:"insured_name",          label:"Insured",          align:undefined },
  { key:"claimant_name",         label:"Claimant",         align:undefined },
  { key:"lead_adjuster_name",    label:"Lead",             align:undefined },
  { key:"contributor_names",     label:"Contributors",     align:undefined },
  { key:"unbilled_hours",        label:"Unbilled Hrs",     align:"right"   },
  { key:"unbilled_expense_value",label:"Unbilled Exp.",    align:"right"   },
  { key:"est_billable_value",    label:"Est. Billable",    align:"right"   },
  { key:"last_entry_date",       label:"Last Entry",       align:undefined },
  { key:"wip_age_days",          label:"WIP Age",          align:"right"   },
];

// RP-4 (2026-05-08): Claim Payment History columns.  Reads native `payments`
// table (lifted from vca_payments in Chunk 11).  Self-scoped by default;
// View-as picker for admins.  Distinct from PAYMENT_HIST_COLUMNS above which
// is for the empty `invoices` vendor-bill table (Phase 4 scope).
const CLAIM_PAYMENT_COLUMNS = [
  { key:"claim_number",       label:"Claim #",            align:undefined },
  { key:"insured_name",       label:"Insured",            align:undefined },
  { key:"claimant_name",      label:"Claimant",           align:undefined },
  { key:"lead_adjuster_name", label:"Lead",               align:undefined },
  { key:"business_line",      label:"Line",               align:undefined },
  { key:"payment_date",       label:"Date",               align:undefined },
  { key:"amount",             label:"Amount",             align:"right"   },
  { key:"payee",              label:"Payee",              align:undefined },
  { key:"payment_method",     label:"Method",             align:undefined },
  { key:"cheque_number_raw",  label:"Cheque #",           align:undefined },
  { key:"note",               label:"Note",               align:undefined },
];

// RP-3 (2026-05-08): Team Rollup mode column set.  Per-lead-adjuster aggregation
// across all of their claims with any unbilled WIP.
const WIP_ROLLUP_COLUMNS = [
  { key:"lead_adjuster_name",    label:"Adjuster",         align:undefined },
  { key:"claim_count",           label:"Claims",           align:"right"   },
  { key:"total_hours",           label:"Hours",            align:"right"   },
  { key:"total_time_value",      label:"Time $",           align:"right"   },
  { key:"total_expense_value",   label:"Expenses $",       align:"right"   },
  { key:"total_est_billable",    label:"Est. Billable",    align:"right"   },
  { key:"oldest_wip_days",       label:"Oldest WIP",       align:"right"   },
];

// ACS (Advanced Claim Search) v1 — 12-column locked result shape per Kuhrt's
// 2026-05-12 spec.  Insured intentionally before Claimant (canonical post
// INSURED-LIFT-1: Insured = policy-holding company, Claimant = injured
// crewmember).  Trio uses canonical UI-WORKBENCH-1.1 singular labels.
const ACS_COLUMNS = [
  { key:"claim_number",       label:"Claim #",           align:undefined },
  { key:"insured_name",       label:"Insured",           align:undefined },
  { key:"claimant_name",      label:"Claimant",          align:undefined },
  { key:"lead_adjuster_name", label:"Lead Adjuster",     align:undefined },
  { key:"vessel_name",        label:"Vessel",            align:undefined },
  { key:"claim_status",       label:"Status",            align:undefined },
  { key:"claim_type",         label:"Category",          align:undefined },
  { key:"date_opened",        label:"Date Opened",       align:undefined },
  { key:"date_of_loss",       label:"DOL",               align:undefined },
  { key:"total_reserves",     label:"Total Reserve",     align:"right"   },
  { key:"total_paid",         label:"Total Payments",    align:"right"   },
  { key:"remaining_reserves", label:"Remaining Reserve", align:"right"   },
];

// ACS default filter state — all axes empty/false.  Reset button restores this.
const ACS_DEFAULT_FILTERS = {
  insured_company_id:          "",
  claimant_q:                  "",
  rep_q:                       "",
  vessel_id:                   "",
  claim_number_q:              "",
  status:                      "",
  claim_type:                  "",
  loss_state:                  "",  // "Loss Location - State" — ACS-FOLLOWUP-STATE-JURISDICTION
  lead_adjuster_id:            "",  // hidden from self-only viewers
  contributor_user_id:         "",  // hidden from self-only viewers
  underwriter_company_id:      "",
  lead_underwriter_company_id: "",
  dol_from:                    "",
  dol_to:                      "",
  date_opened_from:            "",
  date_opened_to:              "",
  date_closed_from:            "",
  date_closed_to:              "",
  has_open_reserves:           false,
  closed_with_remaining:       false,
  paid_gt_50k:                 false,
  no_payments:                 false,
  litigation_flag:             false,
};

// ── UI-SWEEP-REPORTS (Design Standard Part IV, Template D) ──────────────────
// One visual scaffold shared by the six INLINE reports (loss-run,
// reimbursement-status, open-closed, adjuster-wip, claim-payments,
// advanced-claim-search): (1) a filters card — the report's filter rows
// stacked on the left, the Run/Reset/Export action cluster pinned upper-right
// (Law 5: primary action upper-right); (2) the KPI band slot; (3) the results.
// Purely presentational — slot content is each report's existing JSX moved in.
// Renders its own .list-header wrapper so the .page.list-view flex contract
// (fixed header band + scrolling .list-body) is preserved: children is the
// existing .list-body element, passed through as a sibling via the fragment.
function ReportScaffold({ filters, actions, kpis, children }) {
  return (
    <>
      <div className="list-header">
        <div className="card" style={{ padding: "12px 14px", marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
            <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 8 }}>
              {filters}
            </div>
            {actions && (
              <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap", flexShrink: 0, maxWidth: 400 }}>
                {actions}
              </div>
            )}
          </div>
        </div>
        {kpis}
      </div>
      {children}
    </>
  );
}

export default function Reports({ onSelectClaim }) {
  const [activeReport, setActiveReport] = useState("loss-run");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  // REPORTS-UX-1: participate in nav as first-class report VIEWS. The bridge
  // (useReportNav, wired below once all report state exists) keeps the current
  // nav location's {report, view, label} in sync with the live report so a
  // pin/recent/saved-view restores the exact view (e.g. "WIP — Team Rollup").
  const { current, navigate } = useNavigation();
  // Self-contained reports (Productivity / Production by Party / Bordereau /
  // Reimbursement Requests) emit their serialized view state up so it can be
  // captured + restored alongside the Reports.js-owned reports.
  const [selfContainedVS, setSelfContainedVS] = useState({});
  // Bumped when an incoming pinned/saved view is applied to a self-contained
  // report, forcing a remount so its useState re-seeds from the restored view
  // (their internal state can't be set from here). NOT bumped on internal edits.
  const [scvRestoreKey, setScvRestoreKey] = useState(0);
  const [statusFilter, setStatusFilter] = useState("Open");
  const [roleFilter, setRoleFilter]     = useState("all"); // RP-1: lead | staff | all
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sort, setSort] = useState({ key: "claim_number", dir: "asc" });
  // RP-1.1: cap-gated "View as" picker.  null = self.  Backend silently
  // ignores user_id from non-team-reports users, but the picker is hidden
  // entirely from ordinary adjusters per Kuhrt's role-privacy spec.
  // Cap migrated from manage_users → view_team_reports 2026-05-11
  // (RP-PERMISSIONS-CONSOLIDATE).
  const [viewAsUserId, setViewAsUserId] = useState(null);
  const [adjusterList, setAdjusterList] = useState(null); // null until lazily loaded
  const canPickUser = can("view_team_reports");
  const meId = getUser()?.id;
  // WIP "Trigger Invoice" is offered only to the claim's own lead adjuster, or to
  // manager-and-above (view_team_reports = manager / exec / ownership / admin). The
  // submit-invoice-prep endpoint remains the server-side authority. (Kuhrt 2026-06-05)
  const canTriggerRow = (r) => canPickUser || r.lead_adjuster_id === meId;

  // RP-2: Adjuster WIP state.
  const [wipMode, setWipMode] = useState("worked"); // worked | leading
  const [wipCounts, setWipCounts] = useState(null); // {total_claims, total_hours, total_time_value, total_expense_value, total_est_billable}
  const [expandedClaimId, setExpandedClaimId] = useState(null);
  const [drilldownCache, setDrilldownCache] = useState({}); // {[claimId]: {time, expenses}}
  const [triggerBusy, setTriggerBusy] = useState(null); // REVOPS: claimId being toggled for invoicing
  // RP-4 v2 (2026-05-08): consolidated filter state in one object.
  // Lives behind the reusable <PaymentHistoryFilters> component so the same
  // shape works in this Reports view AND in future per-claim Payment History
  // tabs (where claimIdLocked + filter state still apply).
  const [phFilters, setPhFilters]         = useState(DEFAULT_PH_FILTERS);
  const [paymentCounts, setPaymentCounts] = useState(null);
  const [paymentTotal, setPaymentTotal] = useState(0);
  const [paymentFacets, setPaymentFacets] = useState(null);

  // RP-LOSS-RUN-POLISH v1 (2026-05-12): Loss Run filter state.  Status is
  // shared with other reports via statusFilter (default "Open" already set).
  // Insured/Vessel/Claim Category are loss-run-specific.  Empty string
  // means "All".  Facets feed dropdowns; scope-aware backend.
  const [lossRunInsuredId, setLossRunInsuredId] = useState("");
  // RP-LOSSRUN-MULTI (2026-07-10): Loss Run Insured filter is now one-or-more
  // (array of company-id strings; empty = All).  lossRunInsuredId above stays
  // for the Reimbursement Status report, which still single-selects.
  const [lossRunInsuredIds, setLossRunInsuredIds] = useState([]);
  // One-or-more Underwriters (carriers on the slip) — Kuhrt's "insurers" ask.
  const [lossRunUnderwriterIds, setLossRunUnderwriterIds] = useState([]);
  const [lossRunPolicyId,  setLossRunPolicyId]  = useState(""); // specific-policy filter
  const [reimbUwId, setReimbUwId] = useState(""); // Reimbursement Status underwriter filter
  const [lossRunVesselId,  setLossRunVesselId]  = useState("");
  const [lossRunCategory,  setLossRunCategory]  = useState("");
  const [lossRunBusinessLine, setLossRunBusinessLine] = useState("");
  // POL-FROMTO-V1 (2026-05-18): Policy From / Policy To enabled now that
  // Policy & Coverage v1 is live.  Semantic per Kuhrt lock: filter claims
  // by the period of the attached policy (overlap match).  Claims without
  // a dated attached policy are excluded from strict policy-period results
  // — tooltip discloses this.
  const [lossRunPolicyFrom, setLossRunPolicyFrom] = useState("");
  const [lossRunPolicyTo,   setLossRunPolicyTo]   = useState("");
  const [lossRunFacets,    setLossRunFacets]    = useState(null);
  // RP-LOSS-RUN-COLS (2026-06-18): optional Loss Run columns the user can show.
  const [lossRunCols,      setLossRunCols]      = useState(() => new Set());

  // ACS v1 state (2026-05-12).
  const [acsFilters,   setAcsFilters]   = useState(ACS_DEFAULT_FILTERS);
  const [acsFacets,    setAcsFacets]    = useState(null);
  const [acsDateOpen,  setAcsDateOpen]  = useState(false); // Date Filters expander
  const [acsError,     setAcsError]     = useState(null);  // inline no-filter validation

  // ── REPORTS-UX-1 state↔params bridge ──────────────────────────────────────
  // Live serialized view-state for the active report. Self-contained reports
  // inject theirs via selfContainedVS (emitted by onViewStateChange).
  const viewState = useMemo(() => {
    const r = REPORTS[activeReport];
    if (!r) return {};
    return r.serialize({
      wipMode, statusFilter, roleFilter, viewAsUserId, sort, dateFrom, dateTo,
      lossRunInsuredId, lossRunInsuredIds, lossRunUnderwriterIds, lossRunPolicyId, lossRunVesselId, lossRunCategory, lossRunBusinessLine, lossRunPolicyFrom, lossRunPolicyTo,
      reimbUwId, phFilters, acsFilters, ...selfContainedVS,
    });
  }, [activeReport, wipMode, statusFilter, roleFilter, viewAsUserId, sort, dateFrom, dateTo,
      lossRunInsuredId, lossRunInsuredIds, lossRunUnderwriterIds, lossRunPolicyId, lossRunVesselId, lossRunCategory, lossRunBusinessLine, lossRunPolicyFrom, lossRunPolicyTo,
      reimbUwId, phFilters, acsFilters, selfContainedVS]);
  // Setter bag passed to a report's applyParams() when restoring an incoming view.
  const reportSetters = useMemo(() => ({
    setWipMode, setStatusFilter, setRoleFilter, setViewAsUserId, setSort, setDateFrom, setDateTo,
    setLossRunInsuredId, setLossRunInsuredIds, setLossRunUnderwriterIds, setLossRunPolicyId, setLossRunVesselId, setLossRunCategory, setLossRunBusinessLine, setLossRunPolicyFrom,
    setLossRunPolicyTo, setReimbUwId, setPhFilters, setAcsFilters,
  }), []);
  const applyIncoming = useCallback((rid, params) => {
    const r = REPORTS[rid];
    if (r?.selfContained) setScvRestoreKey((k) => k + 1); // remount → re-seed from restored view
    else r?.applyParams(params, reportSetters);
  }, [reportSetters]);
  useReportNav({ activeReport, viewState, setActiveReport, applyIncoming });
  // Graceful mode-gating: a restored view may carry WIP "rollup" for a user who
  // lost team-reports access — fall back to "worked" (server also self-scopes).
  useEffect(() => {
    if (activeReport === "adjuster-wip" && wipMode === "rollup" && !canPickUser) setWipMode("worked");
    /* eslint-disable-next-line */
  }, [activeReport, wipMode, canPickUser]);
  // Workspace metadata for the active report + its per-user saved views.
  const activeReg = REPORTS[activeReport] || {};
  const accessible = reportVisible(activeReport);
  const { views: savedViews, save: saveView, remove: removeView } = useSavedViews(activeReport);

  // Lazily load the adjuster list when an admin first opens OCC, WIP, or RP-4.
  useEffect(() => {
    if (activeReport !== "open-closed" && activeReport !== "adjuster-wip" && activeReport !== "claim-payments") return;
    if (!canPickUser) return;
    if (adjusterList !== null) return;
    apiFetch("/users/with-claim-assignments")
      .then(d => setAdjusterList(d || []))
      .catch(() => setAdjusterList([]));
  }, [activeReport, canPickUser, adjusterList]);

  // RP-LOSS-RUN-POLISH v1: Loss Run facets (Insured / Vessel / Claim Category
  // dropdowns).  Scope-aware backend — self-only viewers see only options
  // derived from their visible claims.
  //
  // Polish 2026-05-12 (dependent Vessel filter): refetch when the Insured
  // filter changes so Vessel narrows to that Insured's claims.  Insured +
  // Claim Category options are NOT narrowed by Insured (asymmetric on
  // purpose per Kuhrt's polish review — the user needs the full Insured
  // catalog to browse).  If the current Vessel selection isn't in the
  // narrower options, reset it to All Vessels.
  // NOTE: lossRunVesselId is intentionally NOT in the deps array — adding
  // it would create a refetch loop (reset → effect re-runs → reset again).
  // The validation reads the value captured at fetch-trigger time, which
  // is the right semantic.
  useEffect(() => {
    // Reimbursement Status reuses the Loss Run Insured facet for its picker
    // (still single-select via lossRunInsuredId); Loss Run itself narrows by
    // the multi-select (RP-LOSSRUN-MULTI).  Vessel AND Policy options narrow
    // to the selected Insured(s); selections no longer in the narrowed
    // options auto-reset (same closure-capture semantic as before).
    if (activeReport !== "loss-run" && activeReport !== "reimbursement-status") return;
    const params = new URLSearchParams();
    if (activeReport === "loss-run") {
      if (lossRunInsuredIds.length) params.set("insured_company_ids", lossRunInsuredIds.join(","));
    } else if (lossRunInsuredId) {
      params.set("insured_company_id", String(lossRunInsuredId));
    }
    apiFetch(`/reports/loss-run/facets?${params.toString()}`)
      .then(d => {
        const f = d || {insured_options:[], vessel_options:[], policy_options:[], claim_type_options:[]};
        setLossRunFacets(f);
        if (lossRunVesselId && !(f.vessel_options || []).some(v => String(v.vessel_id) === String(lossRunVesselId))) {
          setLossRunVesselId("");
        }
        if (lossRunPolicyId && !(f.policy_options || []).some(p => String(p.policy_id) === String(lossRunPolicyId))) {
          setLossRunPolicyId("");
        }
      })
      .catch(() => setLossRunFacets({insured_options:[], vessel_options:[], policy_options:[], claim_type_options:[]}));
    /* eslint-disable-next-line */
  }, [activeReport, lossRunInsuredId, lossRunInsuredIds]);

  // ACS v1: fetch facets on entry and refetch when Insured changes (dependent
  // Vessel narrowing).  Peer-axis facet sets (lead_adjuster_options,
  // contributor_options) returned ONLY to VIEW_TEAM_REPORTS holders by the
  // backend; the frontend hides those slots for self-only viewers anyway.
  // Vessel selection auto-resets if no longer in narrowed options.
  // acsFilters.vessel_id intentionally NOT in deps — adding it would create
  // a reset loop.  Validation closure-captures the value at fetch-trigger time.
  useEffect(() => {
    if (activeReport !== "advanced-claim-search") return;
    const params = new URLSearchParams();
    if (acsFilters.insured_company_id) params.set("insured_company_id", String(acsFilters.insured_company_id));
    apiFetch(`/reports/claim-search/facets?${params.toString()}`)
      .then(d => {
        const f = d || {};
        setAcsFacets(f);
        if (acsFilters.vessel_id && !(f.vessel_options || []).some(v => String(v.vessel_id) === String(acsFilters.vessel_id))) {
          setAcsFilters(prev => ({ ...prev, vessel_id: "" }));
        }
      })
      .catch(() => setAcsFacets({insured_options:[],vessel_options:[],status_options:[],claim_type_options:[],underwriter_options:[],lead_underwriter_options:[]}));
    /* eslint-disable-next-line */
  }, [activeReport, acsFilters.insured_company_id]);

  // RP-4 v2: lazily load Claim # facets for the datalist autocomplete.
  // Refetch when the Lead Adjuster filter changes so the dropdown narrows
  // to that adjuster's book.
  useEffect(() => {
    if (activeReport !== "claim-payments") return;
    const params = new URLSearchParams();
    if (canPickUser && phFilters.lead_adjuster_id) params.set("user_id", String(phFilters.lead_adjuster_id));
    apiFetch(`/reports/claim-payment-history/facets?${params.toString()}`)
      .then(d => setPaymentFacets(d || {claim_numbers:[]}))
      .catch(() => setPaymentFacets({claim_numbers:[]}));
  }, [activeReport, phFilters.lead_adjuster_id, canPickUser]);

  const run = () => {
    // Productivity is a self-contained component (RP-5) — owns its own
    // fetch + render; nothing to do here.
    if (activeReport === "productivity") return;
    if (activeReport === "production-by-party") return;  // self-contained mgmt report
    // Bordereau is a self-contained component (BDX build) — owns its own
    // fetch + render via the /reports/bordereau/* endpoints.
    if (activeReport === "bordereau") return;
    // Reimbursement Requests (TRUST-REIMBURSEMENTS-2) is self-contained too.
    if (activeReport === "reimbursement-requests") return;
    setLoading(true);
    setExpandedClaimId(null);
    setDrilldownCache({});
    let url;
    if (activeReport === "loss-run") {
      // RP-LOSS-RUN-POLISH v1: combinable filters (Insured / Status / Vessel
      // / Claim Category).  POL-FROMTO-V1 (2026-05-18) added Policy From /
      // Policy To filters now that Policy & Coverage v1 is live.  As of Date
      // remains disabled pending reserve-history as-of math validation.
      const params = new URLSearchParams();
      if (statusFilter)             params.set("status",              statusFilter);
      if (lossRunInsuredIds.length) params.set("insured_company_ids", lossRunInsuredIds.join(","));
      if (lossRunUnderwriterIds.length) params.set("underwriter_company_ids", lossRunUnderwriterIds.join(","));
      if (lossRunVesselId)          params.set("vessel_id",           String(lossRunVesselId));
      if (lossRunPolicyId)          params.set("policy_id",           String(lossRunPolicyId));
      if (lossRunCategory)          params.set("claim_type",          lossRunCategory);
      if (lossRunBusinessLine)      params.set("business_line",       lossRunBusinessLine);
      if (lossRunPolicyFrom)        params.set("policy_from",         lossRunPolicyFrom);
      if (lossRunPolicyTo)          params.set("policy_to",           lossRunPolicyTo);
      url = `/reports/loss-run?${params.toString()}`;
    } else if (activeReport === "reimbursement-status") {
      // REPORTING-PHASE-2 (2026-05-29): defaults to Open claims.  Blank
      // statusFilter → backend default (Open); "all" → every status.
      const params = new URLSearchParams();
      params.set("status", statusFilter === "" ? "Open" : statusFilter);
      if (lossRunInsuredId)  params.set("insured_company_id", String(lossRunInsuredId));
      if (reimbUwId)         params.set("underwriter_company_id", String(reimbUwId));
      if (lossRunPolicyFrom) params.set("policy_from",        lossRunPolicyFrom);
      if (lossRunPolicyTo)   params.set("policy_to",          lossRunPolicyTo);
      url = `/reports/reimbursement-status?${params.toString()}`;
    } else if (activeReport === "advanced-claim-search") {
      // ACS v1: combinable filters across ~17 axes.  Inline no-filter
      // validation enforced here so the fetch never fires with zero filters
      // (Workbench is the broad-browse surface).  Audit dedup is offset-based
      // backend-side: this Run-Search click sends offset=0 → backend logs
      // exactly one phi_access_log row.  Subsequent pagination would send
      // offset>0 and stay silent.  v1 doesn't paginate yet; full-table fetch
      // capped at backend's 500-row max.
      const f = acsFilters;
      const activeCount = [
        f.insured_company_id, f.claimant_q, f.rep_q, f.vessel_id, f.claim_number_q,
        f.status, f.claim_type, f.loss_state, f.lead_adjuster_id, f.contributor_user_id,
        f.underwriter_company_id, f.lead_underwriter_company_id,
        f.dol_from, f.dol_to, f.date_opened_from, f.date_opened_to,
        f.date_closed_from, f.date_closed_to,
      ].filter(v => v !== "" && v != null).length
       + [f.has_open_reserves, f.closed_with_remaining, f.paid_gt_50k, f.no_payments, f.litigation_flag].filter(Boolean).length;
      if (activeCount === 0) {
        setAcsError("Add at least one filter to run an advanced search.");
        setLoading(false);
        return;
      }
      setAcsError(null);
      const params = new URLSearchParams();
      Object.entries(f).forEach(([k, v]) => {
        if (typeof v === "boolean") { if (v) params.set(k, "true"); }
        else if (v !== "" && v != null) params.set(k, String(v));
      });
      params.set("offset", "0"); // Run Search always sends offset=0 (audit trigger)
      url = `/reports/claim-search?${params.toString()}`;
    } else if (activeReport === "open-closed") {
      const params = new URLSearchParams();
      if (statusFilter)               params.set("status",  statusFilter);
      if (roleFilter)                 params.set("role",    roleFilter);
      if (dateFrom)                   params.set("dol_from", dateFrom);
      if (dateTo)                     params.set("dol_to",   dateTo);
      if (canPickUser && viewAsUserId) params.set("user_id", String(viewAsUserId));
      url = `/reports/open-closed-claims?${params.toString()}`;
    } else if (activeReport === "adjuster-wip") {
      const params = new URLSearchParams();
      params.set("mode", wipMode);
      if (statusFilter)                params.set("status",  statusFilter);
      if (dateFrom)                    params.set("dol_from", dateFrom);
      if (dateTo)                      params.set("dol_to",   dateTo);
      if (canPickUser && viewAsUserId) params.set("user_id", String(viewAsUserId));
      url = `/reports/adjuster-wip?${params.toString()}`;
    } else if (activeReport === "claim-payments") {
      // RP-4 v2: combinable filter state lives in phFilters; query builder
      // shared with the future per-claim Payment History tab.
      const params = buildPaymentHistoryQuery(phFilters, /*claimIdLocked=*/null);
      params.set("limit", "1000"); params.set("offset", "0");
      url = `/reports/claim-payment-history?${params.toString()}`;
    } else {
      url = `/reports/payment-history?${dateFrom?`date_from=${dateFrom}`:""}${dateTo?`&date_to=${dateTo}`:""}`;
    }
    apiFetch(url).then(d => {
      if (activeReport === "open-closed") {
        setData(d.rows || []);
      } else if (activeReport === "adjuster-wip") {
        setData(d.rows || []);
        setWipCounts(d.counts || null);
      } else if (activeReport === "claim-payments") {
        setData(d.rows || []);
        setPaymentCounts(d.counts || null);
        setPaymentTotal(d.total_count || 0);
      } else if (activeReport === "advanced-claim-search") {
        setData(d.rows || []);
      } else {
        setData(d);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  // REVOPS (2026-06-04): toggle a claim's unbilled work ON/OFF for invoicing.
  // ON = submit-invoice-prep (entries → Pending Invoice Prep, routed to Admin);
  // OFF = untrigger (entries back to Unbilled). Refetch recomputes the TRIGGERED
  // totals (what the commission adjuster is invoicing vs leaving on the table).
  const toggleTrigger = (r) => {
    setTriggerBusy(r.id);
    const nowTriggered = !r.triggered;
    const path = r.triggered ? "untrigger-invoice-prep" : "submit-invoice-prep";
    apiFetch(`/claims/${r.id}/${path}`, { method: "POST", body: {} })
      .then(() => {
        // Optimistic in-place update instead of a full run(): flip just this row's
        // triggered flag and recompute the "Triggered for invoicing" footer totals
        // locally. Avoids re-running the report (which collapsed the table and bounced
        // the scroll to the top) — the scroll position now stays put. (Kuhrt 2026-06-05)
        const nextRows = data.map(x => (x.id === r.id ? { ...x, triggered: nowTriggered } : x));
        setData(nextRows);
        const trig = nextRows.filter(x => x.triggered);
        setWipCounts(wc => (wc ? {
          ...wc,
          triggered_claims: trig.length,
          triggered_hours: trig.reduce((s, x) => s + (parseFloat(x.unbilled_hours) || 0), 0),
          triggered_expense_value: trig.reduce((s, x) => s + (parseFloat(x.unbilled_expense_value) || 0), 0),
          triggered_est_billable: trig.reduce((s, x) => s + (parseFloat(x.est_billable_value) || 0), 0),
        } : wc));
      })
      .catch(e => window.alert(String(e.message || e)))
      .finally(() => setTriggerBusy(null));
  };

  // RP-2 drilldown loader.  Cached per-claim so re-expanding doesn't refetch.
  const toggleDrilldown = (claimId) => {
    if (expandedClaimId === claimId) {
      setExpandedClaimId(null);
      return;
    }
    setExpandedClaimId(claimId);
    if (drilldownCache[claimId]) return; // already loaded
    apiFetch(`/claims/${claimId}/wip-detail`)
      .then(d => setDrilldownCache(prev => ({ ...prev, [claimId]: d })))
      .catch(() => setDrilldownCache(prev => ({ ...prev, [claimId]: { time: [], expenses: [], _err: true } })));
  };
  // Explicit Run Report only — no auto-load on entry — for reports that
  // are expensive to compute or that benefit from deliberate filtering
  // before the first run:
  //   loss-run        (RP-LOSS-RUN-POLISH v1, 2026-05-12)
  //   claim-payments  (polish 2026-05-12 per Kuhrt — firm-wide payments
  //                    on entry is too expensive to default-run)
  // Other reports keep their existing auto-load-on-report-switch behavior.
  useEffect(() => {
    if (activeReport === "loss-run" || activeReport === "reimbursement-status" || activeReport === "claim-payments" || activeReport === "advanced-claim-search") return;
    run();
    /* eslint-disable-next-line */
  }, [activeReport, wipMode]);

  const totalReserves = data.reduce((s, r) => s + parseFloat(r.total_reserves || 0), 0);
  const totalPaid     = data.reduce((s, r) => s + parseFloat(r.total_paid     || 0), 0);

  // Shared accessor map — numeric/date columns across both reports. Unused
  // keys (e.g. loss-run keys during a payment-history render) are simply
  // never queried because they aren't in the active column list.
  const sortAccessors = useMemo(() => ({
    date_of_loss:   r => r.date_of_loss   ? new Date(r.date_of_loss).getTime()   : null,
    invoice_date:   r => r.invoice_date   ? new Date(r.invoice_date).getTime()   : null,
    payment_date:   r => r.payment_date   ? new Date(r.payment_date).getTime()   : null,
    total_reserves: r => parseFloat(r.total_reserves) || 0,
    total_paid:     r => parseFloat(r.total_paid)     || 0,
    total_amount:   r => parseFloat(r.total_amount)   || 0,
    approved_amount:r => parseFloat(r.approved_amount)|| 0,
    remaining:      r => (parseFloat(r.total_reserves)||0) - (parseFloat(r.total_paid)||0),
    open_tasks:     r => parseInt(r.open_tasks, 10)   || 0,
    // RP-2: WIP-specific accessors.
    unbilled_hours:         r => parseFloat(r.unbilled_hours)         || 0,
    unbilled_expense_value: r => parseFloat(r.unbilled_expense_value) || 0,
    est_billable_value:     r => parseFloat(r.est_billable_value)     || 0,
    last_entry_date:        r => r.last_entry_date ? new Date(r.last_entry_date).getTime() : null,
    wip_age_days:           r => parseInt(r.wip_age_days, 10) || 0,
    // RP-3: Team Rollup accessors.
    claim_count:            r => parseInt(r.claim_count, 10)         || 0,
    total_hours:            r => parseFloat(r.total_hours)            || 0,
    total_time_value:       r => parseFloat(r.total_time_value)       || 0,
    total_expense_value:    r => parseFloat(r.total_expense_value)    || 0,
    total_est_billable:     r => parseFloat(r.total_est_billable)     || 0,
    oldest_wip_days:        r => parseInt(r.oldest_wip_days, 10)      || 0,
    // RP-4: Claim Payment History accessors.
    amount:                 r => parseFloat(r.amount) || 0,
    // ACS accessors (date_opened sort key + numeric remaining_reserves;
    // date_of_loss / total_reserves / total_paid already defined above).
    date_opened:            r => r.date_opened ? new Date(r.date_opened).getTime() : null,
    remaining_reserves:     r => parseFloat(r.remaining_reserves) || 0,
  }), []);

  // RP-LOSS-RUN-COLS: enabled optional Loss Run columns, in canonical order,
  // and the full header list (base, with the optional ones inserted after DOL).
  const lrOpt = LOSS_RUN_OPTIONAL.filter(c => lossRunCols.has(c.key));
  const lossRunHeaderCols = (() => {
    if (!lrOpt.length) return LOSS_RUN_COLUMNS;
    const out = [...LOSS_RUN_COLUMNS];
    const i = out.findIndex(c => c.key === "date_of_loss");
    out.splice(i + 1, 0, ...lrOpt.map(c => ({ key: c.key, label: c.label })));
    return out;
  })();
  // Leading text-column span for the Subtotal/Grand-Total label cell: the 7 base
  // text columns (Claimant … DOL) plus any enabled optional columns (all text).
  const lrLabelSpan = 7 + lrOpt.length;

  const columns = activeReport === "loss-run"             ? LOSS_RUN_COLUMNS
                : activeReport === "reimbursement-status" ? REIMB_STATUS_COLUMNS
                : activeReport === "open-closed"          ? OPEN_CLOSED_COLUMNS
                : activeReport === "adjuster-wip"         ? (wipMode === "rollup" ? WIP_ROLLUP_COLUMNS : WIP_COLUMNS_BASE)
                : activeReport === "claim-payments"       ? CLAIM_PAYMENT_COLUMNS
                : activeReport === "advanced-claim-search"? ACS_COLUMNS
                                                          : PAYMENT_HIST_COLUMNS;
  const sorted  = sortRowsBy(data, sort.key, sort.dir, sortAccessors);

  // Reset sort to first column when switching reports so a stale key doesn't
  // leave the sort indicator on a column that no longer exists.  WIP defaults
  // to oldest-first by WIP age (most actionable for invoice prioritization).
  // RP-3: rollup mode defaults sorted by Est Billable desc (biggest book first).
  // RP-4: claim-payments defaults sorted by Date desc (most recent first).
  useEffect(() => {
    // REPORTS-UX-1: when restoring a pinned/recent/saved view for THIS report,
    // the incoming view already carries the right sort — don't overwrite it.
    if (current.params?.report === activeReport && current.params?.view?.sort) return;
    if (activeReport === "adjuster-wip") {
      setSort(wipMode === "rollup"
        ? { key: "total_est_billable", dir: "desc" }
        : { key: "wip_age_days",       dir: "desc" });
    } else if (activeReport === "claim-payments") {
      setSort({ key: "payment_date", dir: "desc" });
    } else if (activeReport === "advanced-claim-search") {
      // ACS default sort matches backend ORDER BY (date_opened DESC NULLS LAST,
      // claim_number) so the first paint after Run Search agrees with the
      // server-side ordering.  Subsequent header clicks re-sort client-side.
      setSort({ key: "date_opened", dir: "desc" });
    } else if (activeReport === "loss-run") {
      // PAYMENT-HISTORY-REDESIGN C7 (Kuhrt): Loss Run defaults to Date of Loss.
      setSort({ key: "date_of_loss", dir: "desc" });
    } else {
      setSort({ key: columns[0].key, dir: "asc" });
    }
    /* eslint-disable-next-line */
  }, [activeReport, wipMode]);

  // REPORTS-UX-1: Reports is now a left-rail WORKSPACE. The rail lists reports by
  // category (filtered by capability); the header strip carries title/description
  // + Saved Views. The old report-picker dropdown is retired. The .page.list-view
  // main area keeps its .list-header / .list-body scroll contract (do NOT wrap it
  // in a non-flex div — that breaks scrolling).
  const grouped = reportsByCategory();
  const loadMorePayments = () => {
    const params = buildPaymentHistoryQuery(phFilters, null);
    params.set("limit", "1000"); params.set("offset", String(data.length));
    setLoading(true);
    apiFetch(`/reports/claim-payment-history?${params.toString()}`)
      .then(d => { setData(prev => [...prev, ...(d.rows || [])]); setPaymentTotal(d.total_count || 0); setLoading(false); })
      .catch(() => setLoading(false));
  };
  const switchReport = (id) => { setActiveReport(id); setData([]); setWipCounts(null); setPaymentCounts(null); setPaymentTotal(0); };

  // ── UI-SWEEP-REPORTS (Template D): ReportScaffold slot content for the six
  // inline reports. The JSX below is each report's EXISTING filter controls,
  // buttons, and KPI band moved into consistent slots — filter rows stacked on
  // the left, Run (primary) / Reset / Export cluster upper-right, KPI band
  // between filters and results. State variables, fetch logic, sorting,
  // grouping, export payloads, and permission gating are untouched.
  let rsFilters = null, rsActions = null, rsKpis = null;
  if (activeReport === "loss-run") {
    rsFilters = (
      <>
        {/* Loss Run row 1 — Insured(s) | Policy | Status.  RP-LOSSRUN-MULTI
            (2026-07-10): the Insured picker adds to a chip list (one-or-more,
            e.g. every operator in a fleet program); the Policy dropdown
            filters to claims actively attached to one specific policy and
            narrows to the selected Insureds' policies. */}
        <div className="search-bar" style={{marginBottom:0,flexWrap:"wrap",alignItems:"center"}}>
          <select value="" onChange={e=>{ const v = e.target.value; if (v && !lossRunInsuredIds.includes(v)) setLossRunInsuredIds(prev => [...prev, v]); }} style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,background:"white",minWidth:220}} title="Filter by one or more Insureds (claim_parties role='Insured'). Pick again to add another; click × on a chip to remove.">
            <option value="">{lossRunInsuredIds.length ? "+ Add Insured…" : "All Insureds"}</option>
            {(lossRunFacets?.insured_options || []).filter(io => !lossRunInsuredIds.includes(String(io.company_id))).map(io => (
              <option key={io.company_id} value={io.company_id}>{io.company_name}</option>
            ))}
          </select>
          {lossRunInsuredIds.map(id => {
            const io = (lossRunFacets?.insured_options || []).find(o => String(o.company_id) === String(id));
            return (
              <span key={id} style={{display:"inline-flex",alignItems:"center",gap:6,padding:"4px 8px",border:"1px solid var(--border)",borderRadius:12,background:"var(--bg2, #f3f5f8)",fontSize:12}}>
                {io ? io.company_name : `Insured #${id}`}
                <button onClick={()=>setLossRunInsuredIds(prev => prev.filter(x => x !== id))} title="Remove this Insured from the filter" style={{border:"none",background:"none",cursor:"pointer",padding:0,lineHeight:1,fontSize:13,color:"var(--text2)"}}>×</button>
              </span>
            );
          })}
          <select value="" onChange={e=>{ const v = e.target.value; if (v && !lossRunUnderwriterIds.includes(v)) setLossRunUnderwriterIds(prev => [...prev, v]); }} style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,background:"white",minWidth:200}} title="Filter by one or more Underwriters — the carriers on the slip (claim_parties role Underwriter / Lead Underwriter). Pick again to add another; click × on a chip to remove.">
            <option value="">{lossRunUnderwriterIds.length ? "+ Add Underwriter…" : "All Underwriters"}</option>
            {(lossRunFacets?.underwriter_options || []).filter(uo => !lossRunUnderwriterIds.includes(String(uo.company_id))).map(uo => (
              <option key={uo.company_id} value={uo.company_id}>{uo.company_name}</option>
            ))}
          </select>
          {lossRunUnderwriterIds.map(id => {
            const uo = (lossRunFacets?.underwriter_options || []).find(o => String(o.company_id) === String(id));
            return (
              <span key={id} style={{display:"inline-flex",alignItems:"center",gap:6,padding:"4px 8px",border:"1px solid var(--border)",borderRadius:12,background:"var(--bg2, #f3f5f8)",fontSize:12}}>
                {uo ? uo.company_name : `Underwriter #${id}`}
                <button onClick={()=>setLossRunUnderwriterIds(prev => prev.filter(x => x !== id))} title="Remove this Underwriter from the filter" style={{border:"none",background:"none",cursor:"pointer",padding:0,lineHeight:1,fontSize:13,color:"var(--text2)"}}>×</button>
              </span>
            );
          })}
          <select value={lossRunPolicyId} onChange={e=>setLossRunPolicyId(e.target.value)} style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,background:"white",minWidth:200,maxWidth:340}} title="Filter to claims attached to one specific policy (via the claim's Policy & Coverage attachment). Narrows to the selected Insureds' policies.">
            <option value="">All Policies</option>
            {(lossRunFacets?.policy_options || []).map(po => {
              const period = (po.inception_date || po.expiration_date)
                ? ` (${po.inception_date ? String(po.inception_date).slice(0,10) : "?"} → ${po.expiration_date ? String(po.expiration_date).slice(0,10) : "?"})`
                : "";
              const who = po.insured_name ? ` — ${po.insured_name}` : "";
              return <option key={po.policy_id} value={po.policy_id}>{(po.policy_number || `Policy #${po.policy_id}`) + period + who}</option>;
            })}
          </select>
          <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,background:"white"}} title="Filter by claim status">
            <option value="">All Statuses</option>
            <option value="Open">Open</option>
            <option value="Closed">Closed</option>
            <option value="On Hold">On Hold</option>
          </select>
        </div>
        {/* Loss Run row 2 — Vessel / Claim Category / Business Line / Policy
            From / Policy To / disabled As-of Date (RP-LOSS-RUN-POLISH v1 +
            POL-FROMTO-V1). */}
        <div className="search-bar" style={{marginBottom:0}}>
          <select value={lossRunVesselId} onChange={e=>setLossRunVesselId(e.target.value)} style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,background:"white",minWidth:180}} title="Filter by vessel">
            <option value="">All Vessels</option>
            {(lossRunFacets?.vessel_options || []).map(vo => (
              <option key={vo.vessel_id} value={vo.vessel_id}>{vo.vessel_name}</option>
            ))}
          </select>
          {/* RP-LOSSRUN-MULTI (2026-07-10): relabeled for clarity — "Claim Type"
              is the fine-grained type on the claim (the report's "Type" column,
              e.g. "P&I Crew"); "Business Line" is the coarse P&I / H&M / Other
              rollup derived FROM the claim type (the "Line" column). */}
          <select value={lossRunCategory} onChange={e=>setLossRunCategory(e.target.value)} style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,background:"white"}} title="Filter by claim type — the specific type recorded on the claim (the report's Type column, e.g. 'P&I Crew'). Finer-grained than Business Line.">
            <option value="">All Claim Types</option>
            {(lossRunFacets?.claim_type_options || []).map(ct => (
              <option key={ct} value={ct}>{ct}</option>
            ))}
          </select>
          <select value={lossRunBusinessLine} onChange={e=>setLossRunBusinessLine(e.target.value)} style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,background:"white"}} title="Filter by business line — the coarse P&I / H&M / Other rollup derived from the claim type (the report's Line column).">
            <option value="">All Business Lines</option>
            {(lossRunFacets?.business_line_options || ["P&I","H&M","Other"]).map(bl => (
              <option key={bl} value={bl}>{bl}</option>
            ))}
          </select>
          <label title="Filters by the period of the policy attached to the claim. Claims with no attached policy or missing policy dates may not appear in strict policy-period results." style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:12}}>
            <span>Policy From</span>
            <input type="date" value={lossRunPolicyFrom} onChange={e=>setLossRunPolicyFrom(e.target.value)} style={{padding:"6px 8px",border:"1px solid var(--border)",borderRadius:4,background:"white"}}/>
          </label>
          <label title="Filters by the period of the policy attached to the claim. Claims with no attached policy or missing policy dates may not appear in strict policy-period results." style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:12}}>
            <span>Policy To</span>
            <input type="date" value={lossRunPolicyTo} onChange={e=>setLossRunPolicyTo(e.target.value)} style={{padding:"6px 8px",border:"1px solid var(--border)",borderRadius:4,background:"white"}}/>
          </label>
          <label title="As-of reporting is planned. It needs point-in-time reserve &amp; payment reconstruction (a historical snapshot the ledger does not yet compute), so until that is validated the Loss Run reflects current balances." style={{display:"inline-flex",alignItems:"center",gap:6,color:"var(--text3)",cursor:"help",fontSize:12}}>
            <span>As of Date <em style={{fontStyle:"normal",fontSize:10,color:"var(--text3)"}}>(planned)</em></span>
            <input type="date" disabled style={{padding:"6px 8px",border:"1px solid var(--border)",borderRadius:4,background:"#f3f3f5",color:"var(--text3)",cursor:"help"}}/>
          </label>
        </div>
        {/* RP-LOSS-RUN-COLS (2026-06-18): optional column toggles. Off by
            default; update the table immediately (no Run Report needed). */}
        <div className="search-bar" style={{marginBottom:0,flexWrap:"wrap",alignItems:"center",gap:14}}>
          <span style={{fontSize:12,fontWeight:600,color:"var(--text2)"}}>Show columns:</span>
          {LOSS_RUN_OPTIONAL.map(c => (
            <label key={c.key} style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:13,cursor:"pointer"}}>
              <input type="checkbox" checked={lossRunCols.has(c.key)}
                onChange={e => setLossRunCols(prev => { const n = new Set(prev); if (e.target.checked) n.add(c.key); else n.delete(c.key); return n; })}/>
              {c.label}
            </label>
          ))}
        </div>
      </>
    );
    rsActions = (
      <>
        <button className="btn btn-blue" onClick={run}>Run Report</button>
        <button className="btn" onClick={()=>{setStatusFilter("Open");setLossRunInsuredId("");setLossRunInsuredIds([]);setLossRunUnderwriterIds([]);setLossRunPolicyId("");setLossRunVesselId("");setLossRunCategory("");setLossRunBusinessLine("");setLossRunPolicyFrom("");setLossRunPolicyTo("");}} title="Reset filters to defaults (Status=Open, all others All)">Reset</button>
        {/* RP-6 / Reporting Foundation (2026-05-18) — XLSX export.  Disabled
            until at least one query has run; identical filters so browser and
            exported workbook match row-for-row. */}
        <button className="btn btn-outline"
                disabled={!data || data.length === 0}
                title={data && data.length > 0
                  ? `Export ${data.length} row${data.length===1?"":"s"} as XLSX`
                  : "Run the report first; the export uses the same filters."}
                onClick={() => {
                  const params = new URLSearchParams({ format: "xlsx" });
                  if (statusFilter)      params.append("status",             statusFilter);
                  if (lossRunInsuredIds.length) params.append("insured_company_ids", lossRunInsuredIds.join(","));
                  if (lossRunUnderwriterIds.length) params.append("underwriter_company_ids", lossRunUnderwriterIds.join(","));
                  if (lossRunVesselId)   params.append("vessel_id",          String(lossRunVesselId));
                  if (lossRunPolicyId)   params.append("policy_id",          String(lossRunPolicyId));
                  if (lossRunCategory)   params.append("claim_type",         lossRunCategory);
                  if (lossRunBusinessLine) params.append("business_line",    lossRunBusinessLine);
                  if (lossRunPolicyFrom) params.append("policy_from",        lossRunPolicyFrom);
                  if (lossRunPolicyTo)   params.append("policy_to",          lossRunPolicyTo);
                  apiFetch(`/reports/loss-run/export?${params.toString()}`, { raw: true })
                    .then(async (resp) => {
                      const blob = await resp.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      const disp = resp.headers.get("Content-Disposition") || "";
                      const m = /filename="([^"]+)"/.exec(disp);
                      a.download = m ? m[1] : "loss-run.xlsx";
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      setTimeout(() => URL.revokeObjectURL(url), 1000);
                    })
                    .catch(e => alert("Export failed: " + (e.message || e)));
                }}>
          ⤓ Export XLSX
        </button>
      </>
    );
  } else if (activeReport === "reimbursement-status") {
    rsFilters = (
      <>
        <div className="search-bar" style={{marginBottom:0}}>
          <select value={lossRunInsuredId} onChange={e=>setLossRunInsuredId(e.target.value)} style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,background:"white",minWidth:220}} title="Filter by Insured (claim_parties role='Insured')">
            <option value="">All Insureds</option>
            {(lossRunFacets?.insured_options || []).map(io => (
              <option key={io.company_id} value={io.company_id}>{io.company_name}</option>
            ))}
          </select>
          <select value={reimbUwId} onChange={e=>setReimbUwId(e.target.value)} style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,background:"white",minWidth:220}} title="Filter by Underwriter / carrier on the slip">
            <option value="">All Underwriters</option>
            {(lossRunFacets?.underwriter_options || []).map(uo => (
              <option key={uo.company_id} value={uo.company_id}>{uo.company_name}</option>
            ))}
          </select>
          <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,background:"white"}} title="Filter by claim status (defaults to Open)">
            <option value="">Open (default)</option>
            <option value="Closed">Closed</option>
            <option value="On Hold">On Hold</option>
            <option value="all">All Statuses</option>
          </select>
        </div>
        <div className="search-bar" style={{marginBottom:0}}>
          <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} style={{padding:"6px 8px",border:"1px solid var(--border)",borderRadius:4}}/>
          <span style={{fontSize:12,color:"var(--text3)"}}>to</span>
          <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} style={{padding:"6px 8px",border:"1px solid var(--border)",borderRadius:4}}/>
        </div>
      </>
    );
    rsActions = (
      <>
        <button className="btn btn-blue" onClick={run}>Run Report</button>
        <button className="btn btn-outline"
                disabled={!data || data.length === 0}
                title={data && data.length > 0
                  ? `Export ${data.length} row${data.length===1?"":"s"} as XLSX`
                  : "Run the report first; the export uses the same filters."}
                onClick={() => {
                  const params = new URLSearchParams({ format: "xlsx" });
                  params.set("status", statusFilter === "" ? "Open" : statusFilter);
                  if (lossRunInsuredId)  params.append("insured_company_id", String(lossRunInsuredId));
                  if (reimbUwId)         params.append("underwriter_company_id", String(reimbUwId));
                  if (lossRunPolicyFrom) params.append("policy_from",        lossRunPolicyFrom);
                  if (lossRunPolicyTo)   params.append("policy_to",          lossRunPolicyTo);
                  apiFetch(`/reports/reimbursement-status/export?${params.toString()}`, { raw: true })
                    .then(async (resp) => {
                      const blob = await resp.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      const disp = resp.headers.get("Content-Disposition") || "";
                      const m = /filename="([^"]+)"/.exec(disp);
                      a.download = m ? m[1] : "reimbursement-status.xlsx";
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      setTimeout(() => URL.revokeObjectURL(url), 1000);
                    })
                    .catch(e => alert("Export failed: " + (e.message || e)));
                }}>
          ⤓ Export XLSX
        </button>
        <span style={{fontSize:12,color:"var(--text3)"}}>{data.length} records</span>
      </>
    );
  } else if (activeReport === "open-closed") {
    rsFilters = (
      <div className="search-bar" style={{marginBottom:0}}>
        <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,background:"white"}}>
          <option value="">All</option><option value="Open">Open</option><option value="Closed">Closed</option><option value="On Hold">On Hold</option><option value="Litigation">Litigation</option>
        </select>
        <select value={roleFilter} onChange={e=>setRoleFilter(e.target.value)} style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,background:"white"}} title="Role on the claim">
          <option value="all">All my claims</option>
          <option value="lead">Lead adjuster only</option>
          <option value="staff">Following / staff only</option>
        </select>
        {canPickUser && (
          <select value={viewAsUserId || ""} onChange={e=>setViewAsUserId(e.target.value ? Number(e.target.value) : null)} style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,background:"white"}} title="View another user's claims (admin)">
            <option value="">My claims (self)</option>
            {adjusterList === null
              ? <option disabled>Loading...</option>
              : adjusterList.map(u => (
                  <option key={u.id} value={u.id}>{u.full_name} ({u.lead_count} lead{u.staff_count ? ` · ${u.staff_count} staff` : ""})</option>
                ))}
          </select>
        )}
        <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} style={{padding:"6px 8px",border:"1px solid var(--border)",borderRadius:4}}/>
        <span style={{fontSize:12,color:"var(--text3)"}}>to</span>
        <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} style={{padding:"6px 8px",border:"1px solid var(--border)",borderRadius:4}}/>
      </div>
    );
    rsActions = (
      <>
        <button className="btn btn-blue" onClick={run}>Run Report</button>
        <span style={{fontSize:12,color:"var(--text3)"}}>{data.length} records</span>
      </>
    );
  } else if (activeReport === "adjuster-wip") {
    rsFilters = (
      <div className="search-bar" style={{marginBottom:0}}>
        {/* WIP mode pills — filter-like sub-mode toggle (polish review). */}
        <span style={{display:"inline-flex",border:"1px solid var(--border)",borderRadius:4,overflow:"hidden",background:"white"}}>
          {[
            {v:"worked", label:"My Worked",     visible:true},
            {v:"leading",label:"Claims I Lead", visible:true},
            {v:"rollup", label:"Team Rollup",   visible:canPickUser},
          ].filter(m=>m.visible).map(m=>(
            <button key={m.v} onClick={()=>setWipMode(m.v)}
                    style={{padding:"6px 12px",border:"none",fontSize:12,fontWeight:500,
                            background:wipMode===m.v?"var(--navy)":"white",
                            color:wipMode===m.v?"white":"var(--text2)",cursor:"pointer"}}>
              {m.label}
            </button>
          ))}
        </span>
        <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,background:"white"}}>
          <option value="">All</option><option value="Open">Open</option><option value="Closed">Closed</option><option value="On Hold">On Hold</option><option value="Litigation">Litigation</option>
        </select>
        {wipMode!=="rollup" && canPickUser && (
          <select value={viewAsUserId || ""} onChange={e=>setViewAsUserId(e.target.value ? Number(e.target.value) : null)} style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,background:"white"}} title="View another user's claims (admin)">
            <option value="">My claims (self)</option>
            {adjusterList === null
              ? <option disabled>Loading...</option>
              : adjusterList.map(u => (
                  <option key={u.id} value={u.id}>{u.full_name} ({u.lead_count} lead{u.staff_count ? ` · ${u.staff_count} staff` : ""})</option>
                ))}
          </select>
        )}
        <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} style={{padding:"6px 8px",border:"1px solid var(--border)",borderRadius:4}}/>
        <span style={{fontSize:12,color:"var(--text3)"}}>to</span>
        <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} style={{padding:"6px 8px",border:"1px solid var(--border)",borderRadius:4}}/>
      </div>
    );
    rsActions = (
      <>
        <button className="btn btn-blue" onClick={run}>Run Report</button>
        <span style={{fontSize:12,color:"var(--text3)"}}>{data.length} records</span>
      </>
    );
  } else if (activeReport === "claim-payments") {
    // RP-4 v2: the combinable filters (including their Run button) live in the
    // reusable <PaymentHistoryFilters> component shared with the per-claim
    // Payment History tab — rendered whole in the filters slot, unchanged.
    rsFilters = (
      <PaymentHistoryFilters
        filters={phFilters}
        onChange={setPhFilters}
        canPickAdjuster={canPickUser}
        adjusterList={adjusterList}
        facets={paymentFacets}
        onRun={run}
        recordCountLabel={`${data.length} record${data.length===1?"":"s"}`}
      />
    );
    // PAYMENT-HISTORY-REDESIGN C7: Excel + PDF export. Same filters as the
    // browser view (shared backend query builder), so export == what you see.
    const phExport = (fmt) => {
      const params = buildPaymentHistoryQuery(phFilters, null);
      params.set("format", fmt);
      apiFetch(`/reports/claim-payment-history/export?${params.toString()}`, { raw: true })
        .then(async (resp) => {
          const blob = await resp.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          const disp = resp.headers.get("Content-Disposition") || "";
          const m = /filename="([^"]+)"/.exec(disp);
          a.download = m ? m[1] : `payment-history.${fmt}`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 1000);
        })
        .catch(e => alert("Export failed: " + (e.message || e)));
    };
    rsActions = (
      <>
        <button className="btn btn-outline"
                disabled={!data || data.length === 0}
                title={data && data.length > 0 ? "Export the filtered payments as XLSX (frozen header + print titles)"
                                               : "Run the report first; the export uses the same filters."}
                onClick={() => phExport("xlsx")}>⤓ Export XLSX</button>
        <button className="btn btn-outline"
                disabled={!data || data.length === 0}
                title={data && data.length > 0 ? "Export the filtered payments as PDF (first 2,000 rows; XLSX is complete)"
                                               : "Run the report first; the export uses the same filters."}
                onClick={() => phExport("pdf")}>⤓ Export PDF</button>
      </>
    );
  } else if (activeReport === "advanced-claim-search") {
    rsFilters = (
      <>
        {/* ACS row A — primary filter axes (peer-axis filters cap-gated). */}
        <div className="search-bar" style={{marginBottom:0, flexWrap:"wrap"}}>
          <select value={acsFilters.insured_company_id} onChange={e=>setAcsFilters({...acsFilters,insured_company_id:e.target.value})} title="Filter by Insured" style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,background:"white",minWidth:200}}>
            <option value="">All Insureds</option>
            {(acsFacets?.insured_options||[]).map(io=>(<option key={io.company_id} value={io.company_id}>{io.company_name}</option>))}
          </select>
          <select value={acsFilters.status} onChange={e=>setAcsFilters({...acsFilters,status:e.target.value})} title="Filter by claim status" style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,background:"white"}}>
            <option value="">All Statuses</option>
            {(acsFacets?.status_options||[]).map(s=>(<option key={s} value={s}>{s}</option>))}
          </select>
          <input type="text" value={acsFilters.claim_number_q} onChange={e=>setAcsFilters({...acsFilters,claim_number_q:e.target.value})} placeholder="Claim # contains" style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,minWidth:140}}/>
          <input type="text" value={acsFilters.claimant_q} onChange={e=>setAcsFilters({...acsFilters,claimant_q:e.target.value})} placeholder="Claimant name" style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,minWidth:140}}/>
          <input type="text" value={acsFilters.rep_q} onChange={e=>setAcsFilters({...acsFilters,rep_q:e.target.value})} placeholder="Rep name" style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,minWidth:120}}/>
          <select value={acsFilters.vessel_id} onChange={e=>setAcsFilters({...acsFilters,vessel_id:e.target.value})} title={acsFilters.insured_company_id ? "Filter by vessel (narrowed to selected Insured)" : "Filter by vessel"} style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,background:"white",minWidth:160}}>
            <option value="">All Vessels</option>
            {(acsFacets?.vessel_options||[]).map(vo=>(<option key={vo.vessel_id} value={vo.vessel_id}>{vo.vessel_name}</option>))}
          </select>
          <select value={acsFilters.claim_type} onChange={e=>setAcsFilters({...acsFilters,claim_type:e.target.value})} title="Filter by claim category" style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,background:"white"}}>
            <option value="">All Categories</option>
            {(acsFacets?.claim_type_options||[]).map(c=>(<option key={c} value={c}>{c}</option>))}
          </select>
          <select value={acsFilters.loss_state} onChange={e=>setAcsFilters({...acsFilters,loss_state:e.target.value})} title="Filter by Loss Location - State (where the loss occurred; jurisdiction-relevant for adjuster licensing). Distinct from Claimant residence state and Insured company state." style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,background:"white",minWidth:170}}>
            <option value="">All Loss States</option>
            {(acsFacets?.loss_state_options||[]).map(s=>(<option key={s} value={s}>{s}</option>))}
          </select>
          <select value={acsFilters.underwriter_company_id} onChange={e=>setAcsFilters({...acsFilters,underwriter_company_id:e.target.value})} title="Filter by Underwriter" style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,background:"white",minWidth:200}}>
            <option value="">All Underwriters</option>
            {(acsFacets?.underwriter_options||[]).map(uo=>(<option key={uo.company_id} value={uo.company_id}>{uo.company_name}</option>))}
          </select>
          <select value={acsFilters.lead_underwriter_company_id} onChange={e=>setAcsFilters({...acsFilters,lead_underwriter_company_id:e.target.value})} title="Filter by Lead Underwriter" style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,background:"white",minWidth:200}}>
            <option value="">All Lead Underwriters</option>
            {(acsFacets?.lead_underwriter_options||[]).map(uo=>(<option key={uo.company_id} value={uo.company_id}>{uo.company_name}</option>))}
          </select>
          {/* Peer-axis filters: visible only to VIEW_TEAM_REPORTS holders;
              backend also enforces a 403 if these are sent by anyone else. */}
          {canPickUser && (
            <>
              <select value={acsFilters.lead_adjuster_id} onChange={e=>setAcsFilters({...acsFilters,lead_adjuster_id:e.target.value})} title="Filter by Lead Adjuster (firm-wide)" style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,background:"white",minWidth:170}}>
                <option value="">All Lead Adjusters</option>
                {(acsFacets?.lead_adjuster_options||[]).map(u=>(<option key={u.user_id} value={u.user_id}>{u.full_name}</option>))}
              </select>
              <select value={acsFilters.contributor_user_id} onChange={e=>setAcsFilters({...acsFilters,contributor_user_id:e.target.value})} title="Filter by Contributor (anyone who has ever worked the claim)" style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,background:"white",minWidth:170}}>
                <option value="">All Contributors</option>
                {(acsFacets?.contributor_options||[]).map(u=>(<option key={u.user_id} value={u.user_id}>{u.full_name}</option>))}
              </select>
            </>
          )}
        </div>
        {/* ACS row B — Date Filters expander toggle + smart financial chips +
            Litigation toggle. */}
        <div className="search-bar" style={{marginBottom:0, flexWrap:"wrap", alignItems:"center"}}>
          <button type="button" onClick={()=>setAcsDateOpen(!acsDateOpen)} className="btn" style={{padding:"6px 10px"}}>
            Date Filters {acsDateOpen ? "▴" : "▾"}
          </button>
          {[
            {k:"has_open_reserves",     label:"Has Open Reserves"},
            {k:"closed_with_remaining", label:"Closed With Remaining"},
            {k:"paid_gt_50k",           label:"Paid > $50K"},
            {k:"no_payments",           label:"No Payments"},
          ].map(chip => (
            <button key={chip.k} type="button"
                    onClick={()=>setAcsFilters({...acsFilters,[chip.k]:!acsFilters[chip.k]})}
                    style={{padding:"6px 10px",border:"1px solid var(--border)",borderRadius:14,fontSize:12,fontWeight:500,
                            background: acsFilters[chip.k] ? "var(--navy, #1f2d52)" : "white",
                            color: acsFilters[chip.k] ? "white" : "var(--text2, #5a6478)", cursor:"pointer"}}>
              {chip.label}
            </button>
          ))}
          <label style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:12,marginLeft:8,cursor:"pointer"}}>
            <input type="checkbox" checked={acsFilters.litigation_flag} onChange={e=>setAcsFilters({...acsFilters,litigation_flag:e.target.checked})}/>
            Litigation
          </label>
        </div>
        {/* ACS row C — three date range pairs, only when the expander is open. */}
        {acsDateOpen && (
          <div className="search-bar" style={{marginBottom:0, flexWrap:"wrap"}}>
            <label style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:12,color:"var(--text2)"}}>
              <span>DOL</span>
              <input type="date" value={acsFilters.dol_from} onChange={e=>setAcsFilters({...acsFilters,dol_from:e.target.value})}/>
              <span>–</span>
              <input type="date" value={acsFilters.dol_to} onChange={e=>setAcsFilters({...acsFilters,dol_to:e.target.value})}/>
            </label>
            <label style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:12,color:"var(--text2)"}}>
              <span>Date Opened</span>
              <input type="date" value={acsFilters.date_opened_from} onChange={e=>setAcsFilters({...acsFilters,date_opened_from:e.target.value})}/>
              <span>–</span>
              <input type="date" value={acsFilters.date_opened_to} onChange={e=>setAcsFilters({...acsFilters,date_opened_to:e.target.value})}/>
            </label>
            <label style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:12,color:"var(--text2)"}}>
              <span>Closed</span>
              <input type="date" value={acsFilters.date_closed_from} onChange={e=>setAcsFilters({...acsFilters,date_closed_from:e.target.value})}/>
              <span>–</span>
              <input type="date" value={acsFilters.date_closed_to} onChange={e=>setAcsFilters({...acsFilters,date_closed_to:e.target.value})}/>
            </label>
          </div>
        )}
      </>
    );
    rsActions = (
      <>
        <button className="btn btn-blue" onClick={run}>Run Search</button>
        <button className="btn" onClick={()=>{setAcsFilters(ACS_DEFAULT_FILTERS);setAcsError(null);setData([]);setAcsDateOpen(false);}} title="Reset all filters">Reset</button>
        {acsError && <span style={{fontSize:12,color:"#c0392b",fontWeight:500}}>{acsError}</span>}
        {!acsError && data.length > 0 && (
          <span style={{fontSize:12,color:"var(--text3)"}}>{data.length} record{data.length===1?"":"s"}</span>
        )}
      </>
    );
  }

  // KPI-band slot content (moved verbatim from the old .list-header).
  if (activeReport === "adjuster-wip" && wipCounts && data.length > 0) {
    // RP-2 (redesign 2026-06-05): 4 boxes, each with an Unbilled top section and
    // a "Triggered for invoicing" footer.
    rsKpis = (() => {
      const trig = wipMode!=="rollup";
      // Triggered-for-invoicing footer → green sub-line on each cell (LD-DESIGN-010).
      const tsub = (v) => trig ? <span style={{color:"#166534",fontWeight:700}}>Triggered ▸ {v}</span> : null;
      const desc = wipMode==="worked"  ? "with my unbilled work"
                 : wipMode==="leading" ? "I lead with unbilled WIP"
                 : `${wipCounts.total_claims||0} claim${(wipCounts.total_claims||0)===1?"":"s"} across team`;
      return (
        <SummaryRibbon>
          {[
            <RibbonCell key="ct" icon={wipMode==="rollup" ? "👥" : "🛡"}
              label={wipMode==="rollup" ? "Adjusters" : "Claims"}
              value={wipMode==="rollup" ? wipCounts.total_adjusters : wipCounts.total_claims}
              sub={trig
                ? <>{desc} · <span style={{color:"#166534",fontWeight:700}}>Triggered ▸ {wipCounts.triggered_claims||0} / {wipCounts.total_claims||0}</span></>
                : desc} />,
            <RibbonCell key="hrs" icon="⏱" label="Unbilled Hours" color="var(--navy)"
              value={(wipCounts.total_hours||0).toFixed(1)}
              sub={tsub((wipCounts.triggered_hours||0).toFixed(1))} />,
            <RibbonCell key="exp" icon="🧾" label="Unbilled Expenses"
              value={fmt.money(wipCounts.total_expense_value)}
              sub={tsub(fmt.money(wipCounts.triggered_expense_value))} />,
            <RibbonCell key="bill" icon="💰" label="Est. Billable" color="var(--green)"
              value={fmt.money(wipCounts.total_est_billable)}
              sub={tsub(fmt.money(wipCounts.triggered_est_billable))} />,
          ]}
        </SummaryRibbon>
      );
    })();
  } else if (activeReport === "claim-payments") {
    rsKpis = (
      <>
        {paymentCounts && data.length > 0 && (
          <SummaryRibbon>
            {[
              <RibbonCell key="ct" icon="📄" label="Payments" value={paymentCounts.payment_count}
                sub={`${paymentCounts.distinct_claims||0} claim${(paymentCounts.distinct_claims||0)===1?"":"s"}`} />,
              <RibbonCell key="amt" icon="💰" label="Total Amount" color="var(--navy)" value={fmt.money(paymentCounts.total_amount)} />,
              <RibbonCell key="py" icon="👥" label="Distinct Payees" value={paymentCounts.distinct_payees||0} />,
            ]}
          </SummaryRibbon>
        )}
        {paymentTotal > 0 && (
          <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12,fontSize:12,color:"var(--text3)"}}>
            <span>Showing {data.length.toLocaleString()} of {paymentTotal.toLocaleString()} payments</span>
            {data.length < paymentTotal && (
              <button className="btn btn-sm" onClick={loadMorePayments} disabled={loading}>{loading ? "Loading…" : "Load more"}</button>
            )}
          </div>
        )}
      </>
    );
  } else if ((activeReport === "loss-run" || activeReport === "open-closed") && data.length > 0) {
    rsKpis = (() => {
      const openCt   = data.filter(r => r.claim_status === "Open").length;
      const closedCt = data.filter(r => r.claim_status === "Closed").length;
      const rem      = totalReserves - totalPaid;
      return (
        <SummaryRibbon>
          {[
            <RibbonCell key="claims" icon="🛡" label="Claims" value={data.length}
              sub={activeReport==="open-closed" ? `${openCt} open · ${closedCt} closed` : null} />,
            <RibbonCell key="tr" icon="💰" label="Total Reserve" color="var(--navy)" value={fmt.money(totalReserves)} />,
            <RibbonCell key="tp" icon="💵" label="Total Payments" color="var(--green)" value={fmt.money(totalPaid)} />,
            <RibbonCell key="rr" icon="💰" label="Remaining Reserve" value={fmt.money(rem)}
              color={rem < 0 ? "var(--red)" : "var(--green)"} />,
          ]}
        </SummaryRibbon>
      );
    })();
  }

  // The results panel — the existing .list-body element, unchanged. Rendered
  // either inside the scaffold (inline reports) or directly (self-contained
  // reports + UnavailablePanel), keeping the .list-view scroll contract.
  const resultsPanel = (
      <div className="list-body card table-wrap">
        {!accessible ? <UnavailablePanel reportId={activeReport} reportLabel={activeReg.label}
              onRemovePin={() => navigate({ page: "dashboard", params: {} })} />
         : activeReport === "productivity" ? <ProductivityReport key={`prod:${scvRestoreKey}`}
              initialViewState={current.params?.report === "productivity" ? current.params?.view : undefined}
              onViewStateChange={setSelfContainedVS} />
         : activeReport === "production-by-party" ? <ProductionByParty key={`prodparty:${scvRestoreKey}`}
              initialViewState={current.params?.report === "production-by-party" ? current.params?.view : undefined}
              onViewStateChange={setSelfContainedVS} />
         : activeReport === "reimbursement-requests" ? <ReimbursementRequestsReport key={`reimb:${scvRestoreKey}`} onSelectClaim={onSelectClaim}
              initialViewState={current.params?.report === "reimbursement-requests" ? current.params?.view : undefined}
              onViewStateChange={setSelfContainedVS} />
         : activeReport === "bordereau" ? <BordereauReports key={`bdx:${scvRestoreKey}`} onSelectClaim={onSelectClaim}
              initialViewState={current.params?.report === "bordereau" ? current.params?.view : undefined}
              onViewStateChange={setSelfContainedVS} />
         : loading ? <Spinner label="Generating report…" />
         : data.length === 0 ? <EmptyState icon="📊" title={activeReport === "advanced-claim-search" ? "Run a search to see results" : "Run the report to see results"} hint="Set your filters above, then Run." />
         : activeReport === "loss-run" ? (() => {
          // RP-LOSS-RUN-POLISH v1 (2026-05-12): grouped-by-Insured rendering
          // with per-Insured subtotal rows + grand-total row.  Backend
          // ORDER BY insured_name ASC NULLS LAST, claim_number ASC gives the
          // right input order in one pass.
          // PAYMENT-HISTORY-REDESIGN C7: column sort is now a TWO-LEVEL sort
          // (review F6) — groups keep their order and each group's claims sort
          // WITHIN the group. Never a flat re-sort of the grouped array: that
          // would scatter each Insured's rows and shatter the subtotals.
          const groups = [];
          let cur = null;
          for (const r of data) {
            const insured = r.insured_name || "(no Insured)";
            if (!cur || cur.insured_name !== insured) {
              cur = { insured_name: insured, claims: [], total_reserves: 0, remaining_reserves: 0, total_paid: 0, retained: 0, paid_by_policy: 0 };
              groups.push(cur);
            }
            cur.claims.push(r);
            cur.total_reserves     += parseFloat(r.total_reserves)     || 0;
            cur.remaining_reserves += parseFloat(r.remaining_reserves) || 0;
            cur.total_paid         += parseFloat(r.total_paid)         || 0;
            cur.retained           += parseFloat(r.retained)           || 0;
            cur.paid_by_policy     += parseFloat(r.paid_by_policy)     || 0;
          }
          for (const gp of groups) {
            gp.claims = sortRowsBy(gp.claims, sort.key, sort.dir, sortAccessors);
          }
          const grand = groups.reduce((g, gp) => ({
            total_reserves:     g.total_reserves     + gp.total_reserves,
            remaining_reserves: g.remaining_reserves + gp.remaining_reserves,
            total_paid:         g.total_paid         + gp.total_paid,
            retained:           g.retained           + gp.retained,
            paid_by_policy:     g.paid_by_policy     + gp.paid_by_policy,
          }), { total_reserves: 0, remaining_reserves: 0, total_paid: 0, retained: 0, paid_by_policy: 0 });
          const ncols = lossRunHeaderCols.length;
          return (
            <table>
              <thead><tr>
                {lossRunHeaderCols.map(col => (
                  <th key={col.key}
                      title={col.disabledTitle || (col.disabled ? undefined : "Sort by " + col.label + " (within each Insured group)")}
                      onClick={col.disabled ? undefined : () =>
                        setSort(s => ({ key: col.key,
                                        dir: s.key === col.key && s.dir === "asc" ? "desc" : "asc" }))}
                      style={{
                        position:"sticky",
                        top:0,
                        zIndex:2,
                        padding:"10px 12px",
                        textAlign: col.align==="right" ? "right" : "left",
                        fontWeight:700,
                        fontSize:12,
                        letterSpacing:"0.02em",
                        color: col.disabled ? "var(--text3)" : "var(--text1, #1a2438)",
                        fontStyle: col.disabled ? "italic" : undefined,
                        cursor: col.disabled ? "help" : "pointer",
                        userSelect:"none",
                        whiteSpace:"nowrap",
                        background:"#e8ecf2",
                        borderBottom:"2px solid var(--border, #cbd2dd)",
                        boxShadow:"0 2px 4px rgba(0,0,0,0.06)",
                      }}>
                    {col.label}
                    {!col.disabled && sort.key === col.key && (
                      <span style={{marginLeft:4,fontSize:10,color:"var(--blue, #3b6fd4)"}}>{sort.dir === "asc" ? "▲" : "▼"}</span>
                    )}
                    {col.disabled && <span style={{marginLeft:4,fontStyle:"normal",color:"var(--text2, #5a6478)"}}>ⓘ</span>}
                  </th>
                ))}
              </tr></thead>
              <tbody>
                {groups.map((gp, gi) => (
                  <React.Fragment key={gi}>
                    <tr style={{background:"#eef1f6"}}>
                      <td colSpan={ncols} style={{padding:"8px 12px",fontWeight:700,color:"var(--navy, #1f2d52)"}}>
                        {gp.insured_name}
                        <span style={{marginLeft:8,fontSize:11,fontWeight:400,color:"var(--text3)"}}>
                          ({gp.claims.length} claim{gp.claims.length===1?"":"s"})
                        </span>
                      </td>
                    </tr>
                    {gp.claims.map((r,i) => (
                      <tr key={`${gi}-${i}`}>
                        <td>{r.claimant_name || "—"}</td>
                        <td title={r.claim_number} style={{whiteSpace:"nowrap"}}>
                          <span className="mono" style={{color:"var(--blue)",fontWeight:600,fontSize:11}}>{r.claim_number}</span>
                        </td>
                        <td>{r.vessel_name || "—"}</td>
                        <td><StatusPill status={r.claim_status} /></td>
                        <td>{r.business_line || "—"}</td>
                        <td>{r.claim_type_label || r.claim_type || "—"}</td>
                        <td style={{whiteSpace:"nowrap"}}>{fmt.date(r.date_of_loss)}</td>
                        {lrOpt.map(c => { const val = c.render(r); return (
                          <td key={c.key} style={c.key==="loss_description" ? {maxWidth:320} : undefined}
                              title={typeof val==="string" && val!=="—" ? val : undefined}>{val}</td>
                        ); })}
                        <td className="text-right mono">{fmt.money(r.total_reserves)}</td>
                        <td className="text-right mono">{fmt.money(r.remaining_reserves)}</td>
                        <td className="text-right mono">{fmt.money(r.total_paid)}</td>
                        <td className="text-right mono">{renderDeductibleCell(r)}</td>
                        <td className="text-right mono">{fmt.money(r.retained)}</td>
                        <td className="text-right mono">{fmt.money(r.paid_by_policy)}</td>
                      </tr>
                    ))}
                    <tr style={{borderTop:"1px solid var(--border, #cbd2dd)",background:"#ecf0f6"}}>
                      <td colSpan={lrLabelSpan} style={{padding:"8px 12px",textAlign:"right",fontWeight:700,fontStyle:"italic",color:"var(--text2, #5a6478)"}}>
                        Subtotal — {gp.insured_name}
                      </td>
                      <td className="text-right mono" style={{fontWeight:700}}>{fmt.money(gp.total_reserves)}</td>
                      <td className="text-right mono" style={{fontWeight:700}}>{fmt.money(gp.remaining_reserves)}</td>
                      <td className="text-right mono" style={{fontWeight:700}}>{fmt.money(gp.total_paid)}</td>
                      {/* POL-6: subtotal blank for Deductible — sum of per-claim deductibles is not meaningful. */}
                      <td></td>
                      <td className="text-right mono" style={{fontWeight:700}}>{fmt.money(gp.retained)}</td>
                      <td className="text-right mono" style={{fontWeight:700}}>{fmt.money(gp.paid_by_policy)}</td>
                    </tr>
                  </React.Fragment>
                ))}
                {groups.length > 0 && (
                  <tr style={{borderTop:"2px solid var(--navy, #1f2d52)",background:"#dde3ec"}}>
                    <td colSpan={lrLabelSpan} style={{padding:"10px 12px",textAlign:"right",fontWeight:700,color:"var(--navy, #1f2d52)"}}>
                      Grand Total — {groups.length} Insured{groups.length===1?"":"s"} · {data.length} claim{data.length===1?"":"s"}
                    </td>
                    <td className="text-right mono" style={{fontWeight:800,fontSize:14}}>{fmt.money(grand.total_reserves)}</td>
                    <td className="text-right mono" style={{fontWeight:800,fontSize:14}}>{fmt.money(grand.remaining_reserves)}</td>
                    <td className="text-right mono" style={{fontWeight:800,fontSize:14}}>{fmt.money(grand.total_paid)}</td>
                    {/* POL-6: grand total blank for Deductible. */}
                    <td></td>
                    <td className="text-right mono" style={{fontWeight:800,fontSize:14}}>{fmt.money(grand.retained)}</td>
                    <td className="text-right mono" style={{fontWeight:800,fontSize:14}}>{fmt.money(grand.paid_by_policy)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          );
        })()
        : activeReport === "reimbursement-status" ? (() => {
          // REPORTING-PHASE-2 (2026-05-29): grouped-by-Insured render with
          // per-Insured subtotals + grand total.  Backend ORDER BY
          // insured_name, claim_number gives correct input order in one pass.
          const MONEY_KEYS = REIMB_STATUS_COLUMNS.filter(c => c.money).map(c => c.key);
          const zero = () => MONEY_KEYS.reduce((o,k)=>{o[k]=0;return o;}, {});
          const groups = [];
          let cur = null;
          for (const r of data) {
            const insured = r.insured_name || "(no Insured)";
            if (!cur || cur.insured_name !== insured) {
              cur = { insured_name: insured, claims: [], tot: zero() };
              groups.push(cur);
            }
            cur.claims.push(r);
            MONEY_KEYS.forEach(k => { cur.tot[k] += parseFloat(r[k]) || 0; });
          }
          const grand = groups.reduce((g, gp) => {
            MONEY_KEYS.forEach(k => { g[k] += gp.tot[k]; });
            return g;
          }, zero());
          const ncols = REIMB_STATUS_COLUMNS.length;
          const TEXT_COLS = 4; // Claimant, Claim #, Lead Adjuster, DOL
          const renderCell = (r, col) => {
            const v = r[col.key];
            if (col.money) return fmt.money(v);
            if (col.key === "date_of_loss") return fmt.date(v);
            if (col.key === "claim_number")
              return <span className="mono" style={{color:"var(--blue)",fontWeight:600,fontSize:11}}>{v}</span>;
            return v || "—";
          };
          return (
            <table>
              <thead><tr>
                {REIMB_STATUS_COLUMNS.map(col => (
                  <th key={col.key} title={col.title || undefined}
                      style={{position:"sticky",top:0,zIndex:2,padding:"10px 12px",
                              textAlign: col.align==="right" ? "right" : "left",
                              fontWeight:700,fontSize:12,letterSpacing:"0.02em",
                              color:"var(--text1, #1a2438)",whiteSpace:"nowrap",
                              background:"#e8ecf2",borderBottom:"2px solid var(--border, #cbd2dd)",
                              boxShadow:"0 2px 4px rgba(0,0,0,0.06)",cursor: col.title ? "help" : undefined}}>
                    {col.label}{col.title && <span style={{marginLeft:4,color:"var(--text2, #5a6478)"}}>ⓘ</span>}
                  </th>
                ))}
              </tr></thead>
              <tbody>
                {groups.map((gp, gi) => (
                  <React.Fragment key={gi}>
                    <tr style={{background:"#eef1f6"}}>
                      <td colSpan={ncols} style={{padding:"8px 12px",fontWeight:700,color:"var(--navy, #1f2d52)"}}>
                        {gp.insured_name}
                        <span style={{marginLeft:8,fontSize:11,fontWeight:400,color:"var(--text3)"}}>
                          ({gp.claims.length} claim{gp.claims.length===1?"":"s"})
                        </span>
                      </td>
                    </tr>
                    {gp.claims.map((r,i) => (
                      <tr key={`${gi}-${i}`}>
                        {REIMB_STATUS_COLUMNS.map(col => (
                          <td key={col.key}
                              className={col.align==="right" ? "text-right mono" : undefined}
                              style={col.key==="net_due_less_pending" ? {fontWeight:600} : undefined}>
                            {renderCell(r, col)}
                          </td>
                        ))}
                      </tr>
                    ))}
                    <tr style={{borderTop:"1px solid var(--border, #cbd2dd)",background:"#ecf0f6"}}>
                      <td colSpan={TEXT_COLS} style={{padding:"8px 12px",textAlign:"right",fontWeight:700,fontStyle:"italic",color:"var(--text2, #5a6478)"}}>
                        Subtotal — {gp.insured_name}
                      </td>
                      {MONEY_KEYS.map(k => (
                        <td key={k} className="text-right mono" style={{fontWeight:700}}>{fmt.money(gp.tot[k])}</td>
                      ))}
                    </tr>
                  </React.Fragment>
                ))}
                {groups.length > 0 && (
                  <tr style={{borderTop:"2px solid var(--navy, #1f2d52)",background:"#dde3ec"}}>
                    <td colSpan={TEXT_COLS} style={{padding:"10px 12px",textAlign:"right",fontWeight:700,color:"var(--navy, #1f2d52)"}}>
                      Grand Total — {groups.length} Insured{groups.length===1?"":"s"} · {data.length} claim{data.length===1?"":"s"}
                    </td>
                    {MONEY_KEYS.map(k => (
                      <td key={k} className="text-right mono" style={{fontWeight:800,fontSize:13}}>{fmt.money(grand[k])}</td>
                    ))}
                  </tr>
                )}
              </tbody>
            </table>
          );
        })()
        : activeReport === "open-closed" ? (
          <table>
            <thead><tr>
              {OPEN_CLOSED_COLUMNS.map(col => (
                <SortableTh key={col.key} label={col.label} col={col.key} align={col.align} sort={sort} setSort={setSort}/>
              ))}
            </tr></thead>
            <tbody>{sorted.map((r,i) => (
              <tr key={r.id || i}>
                <td className="mono" style={{color:"var(--blue)",fontWeight:600}}>{r.claim_number}</td>
                <td><StatusPill status={r.claim_status} /></td>
                <td>{r.claim_type||"—"}</td>
                <td>{r.insured_name||"—"}</td>
                <td>{r.claimant_name||"—"}</td>
                <td style={{whiteSpace:"nowrap"}}>{fmt.date(r.date_of_loss)}</td>
                <td>{r.lead_adjuster_name||"—"}</td>
                <td>{r.vessel_name||"—"}</td>
                <td className="text-right mono">{fmt.money(r.total_reserves)}</td>
                <td className="text-right mono">{fmt.money(r.total_paid)}</td>
                <td className="text-right mono" style={{color:((parseFloat(r.total_reserves)||0)-(parseFloat(r.total_paid)||0))<0?"var(--red)":"inherit",fontWeight:600}}>{fmt.money((parseFloat(r.total_reserves)||0)-(parseFloat(r.total_paid)||0))}</td>
                <td className="text-center">{r.open_tasks>0&&<span className="badge" style={{background:"#fef3c7",color:"#92400e"}}>{r.open_tasks}</span>}</td>
              </tr>
            ))}</tbody>
          </table>
        ) : activeReport === "claim-payments" ? (
          // RP-4 (2026-05-08): Claim Payment History — native `payments` table.
          // RP-4 v1.2: optional group-by claim or insured with per-group subtotals.
          (() => {
            const renderRow = (r, i) => (
              <tr key={r.id || i}>
                <td className="mono" style={{color:"var(--blue)",fontWeight:600}}>{r.claim_number}</td>
                <td>{r.insured_name||"—"}</td>
                <td>{r.claimant_name||"—"}</td>
                <td>{r.lead_adjuster_name||"—"}</td>
                <td>{r.business_line||"—"}</td>
                <td style={{whiteSpace:"nowrap"}}>{fmt.date(r.payment_date)}</td>
                <td className="text-right mono" style={{fontVariantNumeric:"tabular-nums",fontWeight:600}}>{fmt.money(r.amount)}</td>
                <td>{r.payee||"—"}</td>
                <td><span style={{fontSize:11,color:"var(--text2)",textTransform:"uppercase",letterSpacing:0.4}}>{r.payment_method||"—"}</span></td>
                <td className="mono">{r.cheque_number_raw||"—"}</td>
                <td title={r.note||""} style={{maxWidth:280,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",color:"var(--text2)"}}>{r.note||"—"}</td>
              </tr>
            );
            // Build groups when grouping enabled.  Preserve `sorted` order
            // within each group; group order = first-occurrence in `sorted`.
            const groupKeyFor = phFilters.group_by === "claim"
              ? (r => `${r.claim_number||""} — ${r.insured_name||""}`)
              : null;
            return (
              <table>
                <thead><tr>
                  {CLAIM_PAYMENT_COLUMNS.map(col => (
                    <SortableTh key={col.key} label={col.label} col={col.key} align={col.align} sort={sort} setSort={setSort}/>
                  ))}
                </tr></thead>
                <tbody>{
                  groupKeyFor ? (() => {
                    const groups = new Map();
                    sorted.forEach(r => {
                      const k = groupKeyFor(r);
                      if (!groups.has(k)) groups.set(k, []);
                      groups.get(k).push(r);
                    });
                    const blocks = [];
                    let groupIdx = 0;
                    for (const [key, rows] of groups) {
                      const subtotal = rows.reduce((s,r) => s + (parseFloat(r.amount)||0), 0);
                      blocks.push(
                        <tr key={`grp-h-${groupIdx}`} style={{background:"#eef0f4"}}>
                          <td colSpan={CLAIM_PAYMENT_COLUMNS.length} style={{padding:"6px 12px",fontSize:12,fontWeight:600,color:"var(--text)",borderTop:"1px solid var(--border)"}}>
                            <span style={{fontFamily:"JetBrains Mono,monospace"}}>{key}</span>
                            <span style={{color:"var(--text3)",fontWeight:400,marginLeft:12}}>· {rows.length} payment{rows.length===1?"":"s"} · <span className="mono" style={{fontVariantNumeric:"tabular-nums"}}>{fmt.money(subtotal)}</span></span>
                          </td>
                        </tr>
                      );
                      rows.forEach((r,i) => blocks.push(renderRow(r, `${groupIdx}-${i}`)));
                      groupIdx++;
                    }
                    return blocks;
                  })() : sorted.map(renderRow)
                }</tbody>
              </table>
            );
          })()
        ) : activeReport === "adjuster-wip" && wipMode === "rollup" ? (
          // RP-3 (2026-05-08): Team Rollup — per-lead-adjuster aggregation.
          <table>
            <thead><tr>
              {WIP_ROLLUP_COLUMNS.map(col => (
                <SortableTh key={col.key} label={col.label} col={col.key} align={col.align} sort={sort} setSort={setSort}/>
              ))}
            </tr></thead>
            <tbody>{sorted.map((r,i) => (
              <tr key={r.lead_adjuster_id || i}>
                <td style={{fontWeight:600}}>{r.lead_adjuster_name||"—"}</td>
                <td className="text-right mono" style={{fontVariantNumeric:"tabular-nums"}}>{r.claim_count||0}</td>
                <td className="text-right mono" style={{fontVariantNumeric:"tabular-nums"}}>{(parseFloat(r.total_hours)||0).toFixed(1)}</td>
                <td className="text-right mono" style={{fontVariantNumeric:"tabular-nums"}}>{fmt.money(r.total_time_value)}</td>
                <td className="text-right mono" style={{fontVariantNumeric:"tabular-nums"}}>{fmt.money(r.total_expense_value)}</td>
                <td className="text-right mono" style={{fontVariantNumeric:"tabular-nums",fontWeight:600}}>{fmt.money(r.total_est_billable)}</td>
                <td className="text-right mono" style={{fontVariantNumeric:"tabular-nums",color:(r.oldest_wip_days||0)>=90?"var(--red)":(r.oldest_wip_days||0)>=60?"#d97706":"inherit"}}>{r.oldest_wip_days!=null?`${r.oldest_wip_days}d`:"—"}</td>
              </tr>
            ))}</tbody>
          </table>
        ) : activeReport === "adjuster-wip" ? (
          <table>
            <thead><tr>
              <th style={{width:24}}></th>
              {WIP_COLUMNS_BASE.map(col => (
                <SortableTh key={col.key} label={col.label} col={col.key} align={col.align} sort={sort} setSort={setSort}/>
              ))}
              <th style={{width:140}}>Action</th>
            </tr></thead>
            <tbody>{sorted.map((r,i) => {
              const expanded = expandedClaimId === r.id;
              const drill = drilldownCache[r.id];
              const colspan = WIP_COLUMNS_BASE.length + 1 + 1;
              return (
                <React.Fragment key={r.id || i}>
                  <tr style={{cursor:"pointer"}} onClick={()=>toggleDrilldown(r.id)}>
                    <td style={{textAlign:"center",color:"var(--text3)",userSelect:"none"}}>{expanded?"▾":"▸"}</td>
                    <td className="mono" style={{color:"var(--blue)",fontWeight:600}}>{r.claim_number}</td>
                    <td>{r.insured_name||"—"}</td>
                    <td>{r.claimant_name||"—"}</td>
                    <td>{r.lead_adjuster_name||"—"}</td>
                    <td title={r.contributor_names||""} style={{maxWidth:220,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.contributor_names||"—"}</td>
                    <td className="text-right mono" style={{fontVariantNumeric:"tabular-nums"}}>{(parseFloat(r.unbilled_hours)||0).toFixed(1)}</td>
                    <td className="text-right mono" style={{fontVariantNumeric:"tabular-nums"}}>{fmt.money(r.unbilled_expense_value)}</td>
                    <td className="text-right mono" style={{fontVariantNumeric:"tabular-nums",fontWeight:600}}>{fmt.money(r.est_billable_value)}</td>
                    <td style={{whiteSpace:"nowrap"}}>{fmt.date(r.last_entry_date)}</td>
                    <td className="text-right mono" style={{fontVariantNumeric:"tabular-nums",color:(r.wip_age_days||0)>=90?"var(--red)":(r.wip_age_days||0)>=60?"#d97706":"inherit"}}>{r.wip_age_days!=null?`${r.wip_age_days}d`:"—"}</td>
                    {wipMode!=="rollup" && (
                      <td onClick={e=>e.stopPropagation()}>
                        {canTriggerRow(r) ? (
                          <button className={r.triggered ? "btn btn-sm" : "btn btn-outline btn-sm"}
                                  disabled={triggerBusy===r.id}
                                  onClick={()=>toggleTrigger(r)}
                                  title={r.triggered ? "Triggered for invoicing — click to remove" : "Trigger this claim's unbilled work for invoicing"}
                                  style={{fontSize:11,padding:"3px 10px",
                                          ...(r.triggered ? {background:"#16a34a",borderColor:"#16a34a",color:"#fff"} : {})}}>
                            {triggerBusy===r.id ? "…" : (r.triggered ? "✓ Triggered" : "Trigger Invoice")}
                          </button>
                        ) : r.triggered ? (
                          <span className="badge" style={{fontSize:10,background:"#dcfce7",color:"#166534"}} title="Triggered for invoicing">✓ Triggered</span>
                        ) : null}
                      </td>
                    )}
                  </tr>
                  {expanded && (
                    <tr>
                      <td colSpan={colspan} style={{background:"var(--bg)",padding:"10px 18px",borderTop:"1px solid var(--border)"}}>
                        {!drill ? (
                          <div style={{padding:"12px 0"}}><Spinner label="Loading line items…" /></div>
                        ) : drill._err ? (
                          <div className="alert-error" style={{margin:0}}>Failed to load detail</div>
                        ) : (() => {
                          const hasExp = drill.expenses && drill.expenses.length > 0;
                          // RP-2.2 (2026-05-08): billed-to-date context strip for
                          // invoice-prep decision support.  Visually secondary to the
                          // unbilled tables below.  Sources are native columns only
                          // (time_entries.status / expense_entries.billing_status),
                          // not JSONB.  Last invoice date/status omitted by design —
                          // requires the walters_fee_invoices native lift to land first.
                          const bs = drill.billed_summary || {};
                          const billedHrs = parseFloat(bs.billed_hours) || 0;
                          const billedTime = parseFloat(bs.billed_time_amount) || 0;
                          const billedExp = parseFloat(bs.billed_expense_amount) || 0;
                          const hasBilled = billedHrs > 0 || billedTime > 0 || billedExp > 0;
                          // RP-2 (2026-06-05): clearer "Billed to Date" — a blue-accented
                          // context band so the previously-invoiced history reads as distinct
                          // from (and secondary to) the unbilled work tables below it.
                          const BilledStrip = (
                            <div style={{
                              display:"flex",alignItems:"center",gap:16,marginBottom:12,padding:"8px 12px",
                              background:"#eef2fb",border:"1px solid #c7d2e8",borderLeft:"3px solid #2e5cb8",borderRadius:4,fontSize:11.5
                            }}>
                              <div style={{fontSize:10,fontWeight:800,textTransform:"uppercase",letterSpacing:0.5,color:"#2e5cb8",lineHeight:1.15,minWidth:60}}>Billed<br/>to Date</div>
                              {!hasBilled ? (
                                <div style={{color:"var(--text3)",fontStyle:"italic"}}>No billed history on this claim.</div>
                              ) : (
                                <>
                                  <div><span style={{color:"var(--text3)"}}>Hours:</span> <span className="mono" style={{fontVariantNumeric:"tabular-nums",color:"var(--navy)",fontWeight:600}}>{billedHrs.toFixed(1)}</span></div>
                                  <div><span style={{color:"var(--text3)"}}>Time:</span> <span className="mono" style={{fontVariantNumeric:"tabular-nums",color:"var(--navy)",fontWeight:600}}>{fmt.money(billedTime)}</span></div>
                                  <div><span style={{color:"var(--text3)"}}>Expenses:</span> <span className="mono" style={{fontVariantNumeric:"tabular-nums",color:"var(--navy)",fontWeight:600}}>{fmt.money(billedExp)}</span></div>
                                  <div><span style={{color:"var(--text3)"}}>Last billed entry:</span> <span style={{color:"var(--text2)"}}>{bs.last_billed_entry_date ? fmt.date(bs.last_billed_entry_date) : "—"}</span></div>
                                </>
                              )}
                            </div>
                          );
                          // RP-2.1 (2026-05-07): when there are no unbilled expenses,
                          // collapse to single-column layout so time gets full width
                          // and notes are readable.  Compact "no expenses" caption at bottom.
                          const TimeBlock = (
                            <div>
                              <div className="section-title" style={{marginBottom:6,fontSize:11,textTransform:"uppercase",letterSpacing:0.4,color:"var(--text3)"}}>Time entries ({drill.time?.length||0})</div>
                              {(!drill.time || drill.time.length===0) ? <div style={{fontSize:11,color:"var(--text3)"}}>No unbilled time</div> : (
                                <table style={{fontSize:11,tableLayout:"auto",width:"100%"}}>
                                  <thead><tr>
                                    <th style={{textAlign:"left",width:90}}>Date</th>
                                    <th style={{textAlign:"left",width:130}}>Worker</th>
                                    <th style={{textAlign:"left",width:140}}>Type</th>
                                    <th style={{textAlign:"right",width:60}}>Hrs</th>
                                    <th style={{textAlign:"right",width:80}}>Rate</th>
                                    <th style={{textAlign:"right",width:90}}>Amount</th>
                                    <th style={{textAlign:"left"}}>Note</th>
                                  </tr></thead>
                                  <tbody>{drill.time.map(t=>(
                                    <tr key={t.id}>
                                      <td style={{whiteSpace:"nowrap"}}>{fmt.date(t.work_date)}</td>
                                      <td>{t.worker_name||"—"}</td>
                                      <td>{t.rate_type||"—"}</td>
                                      <td className="text-right mono" style={{fontVariantNumeric:"tabular-nums"}}>{(parseFloat(t.hours)||0).toFixed(2)}</td>
                                      <td className="text-right mono" style={{fontVariantNumeric:"tabular-nums"}}>{fmt.money(t.rate)}</td>
                                      <td className="text-right mono" style={{fontVariantNumeric:"tabular-nums"}}>{fmt.money(t.amount)}</td>
                                      <td style={{whiteSpace:"normal",wordBreak:"break-word",color:"var(--text2)",lineHeight:1.45,minWidth:280,maxWidth:600}}>{t.description||"—"}</td>
                                    </tr>
                                  ))}</tbody>
                                </table>
                              )}
                            </div>
                          );
                          const ExpenseBlock = hasExp ? (
                            <div>
                              <div className="section-title" style={{marginBottom:6,fontSize:11,textTransform:"uppercase",letterSpacing:0.4,color:"var(--text3)"}}>Expenses ({drill.expenses.length})</div>
                              <table style={{fontSize:11,tableLayout:"auto",width:"100%"}}>
                                <thead><tr>
                                  <th style={{textAlign:"left",width:90}}>Date</th>
                                  <th style={{textAlign:"left",width:130}}>Worker</th>
                                  <th style={{textAlign:"left",width:140}}>Type</th>
                                  <th style={{textAlign:"right",width:90}}>Amount</th>
                                  <th style={{textAlign:"left"}}>Note</th>
                                </tr></thead>
                                <tbody>{drill.expenses.map(e=>(
                                  <tr key={e.id}>
                                    <td style={{whiteSpace:"nowrap"}}>{fmt.date(e.expense_date)}</td>
                                    <td>{e.worker_name||"—"}</td>
                                    <td>{e.expense_type||"—"}</td>
                                    <td className="text-right mono" style={{fontVariantNumeric:"tabular-nums"}}>{fmt.money(e.amount)}</td>
                                    <td style={{whiteSpace:"normal",wordBreak:"break-word",color:"var(--text2)",lineHeight:1.45,minWidth:240,maxWidth:500}}>{e.description||"—"}</td>
                                  </tr>
                                ))}</tbody>
                              </table>
                            </div>
                          ) : null;
                          // RP-2 (2026-06-05): name the claim at the top of the drawer so it's
                          // unmistakable the line items below belong to THIS claim.
                          const ClaimHeader = (
                            <div style={{display:"flex",alignItems:"baseline",gap:10,marginBottom:10,paddingBottom:7,borderBottom:"2px solid var(--navy,#1a2744)"}}>
                              <span className="mono" style={{fontSize:13,fontWeight:700,color:"var(--navy)"}}>{r.claim_number}</span>
                              <span style={{fontSize:12,color:"var(--text2)"}}>{r.insured_name||r.claimant_name||""}</span>
                              <span style={{marginLeft:"auto",fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:0.5,color:"var(--text3)"}}>Unbilled work in progress</span>
                            </div>
                          );
                          return (
                            <div>
                              {ClaimHeader}
                              {BilledStrip}
                              {hasExp ? (
                                <div style={{display:"grid",gridTemplateColumns:"1.4fr 1fr",gap:14,alignItems:"start"}}>
                                  {TimeBlock}
                                  {ExpenseBlock}
                                </div>
                              ) : (
                                <div>
                                  {TimeBlock}
                                  <div style={{fontSize:11,color:"var(--text3)",marginTop:10,fontStyle:"italic"}}>No unbilled expenses on this claim.</div>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}</tbody>
          </table>
        ) : activeReport === "advanced-claim-search" ? (
          // ACS v1 — 12-column result table.  Client-side sortable headers
          // (matches loss-run / open-closed / claim-payments / wip patterns).
          // Initial sort = date_opened DESC, matching backend ORDER BY so the
          // first paint after Run Search agrees with server ordering; subsequent
          // header clicks re-sort the in-memory result set.  Sticky header
          // (position:sticky/top:0/zIndex:2) keeps column labels visible while
          // scrolling through paginated results.  Claim # clickable through
          // onSelectClaim (wired from App.js).  Litigation pill inline in
          // Status cell when r.litigation_flag=true.
          <table>
            <thead><tr>
              {ACS_COLUMNS.map(col => (
                <SortableTh key={col.key} label={col.label} col={col.key}
                            align={col.align} sort={sort} setSort={setSort}
                            style={{position:"sticky",top:0,zIndex:2,padding:"8px 12px",fontWeight:700,fontSize:12,background:"#e8ecf2",borderBottom:"2px solid var(--border, #cbd2dd)",color:"var(--text1, #1a2438)"}}/>
              ))}
            </tr></thead>
            <tbody>{sorted.map((r, i) => (
              <tr key={r.claim_id || i}>
                <td title={r.claim_number} style={{whiteSpace:"nowrap"}}>
                  {onSelectClaim ? (
                    <span className="mono" style={{color:"var(--blue)",fontWeight:600,fontSize:12,cursor:"pointer",textDecoration:"underline"}}
                          onClick={()=>onSelectClaim(r.claim_id)}>{r.claim_number}</span>
                  ) : (
                    <span className="mono" style={{color:"var(--blue)",fontWeight:600,fontSize:12}}>{r.claim_number}</span>
                  )}
                </td>
                <td>{r.insured_name || "—"}</td>
                <td>{r.claimant_name || "—"}</td>
                <td>{r.lead_adjuster_name || "—"}</td>
                <td>{r.vessel_name || "—"}</td>
                <td>
                  <StatusPill status={r.claim_status} />
                  {r.litigation_flag && (
                    <span title="Claim is in litigation"
                          style={{marginLeft:6,padding:"1px 6px",fontSize:10,fontWeight:700,letterSpacing:"0.04em",background:"#fee2e2",color:"#991b1b",borderRadius:10}}>LIT</span>
                  )}
                </td>
                <td>{r.claim_type || "—"}</td>
                <td style={{whiteSpace:"nowrap"}}>{fmt.date(r.date_opened)}</td>
                <td style={{whiteSpace:"nowrap"}}>{fmt.date(r.date_of_loss)}</td>
                <td className="text-right mono">{fmt.money(r.total_reserves)}</td>
                <td className="text-right mono">{fmt.money(r.total_paid)}</td>
                <td className="text-right mono">{fmt.money(r.remaining_reserves)}</td>
              </tr>
            ))}</tbody>
          </table>
        ) : (
          <table>
            <thead><tr>
              {PAYMENT_HIST_COLUMNS.map(col => (
                <SortableTh key={col.key} label={col.label} col={col.key} align={col.align} sort={sort} setSort={setSort}/>
              ))}
            </tr></thead>
            <tbody>{sorted.map((r,i) => (
              <tr key={i}>
                <td className="mono" style={{color:"var(--blue)",fontWeight:600}}>{r.claim_number}</td>
                <td>{r.insurer||"—"}</td>
                <td className="mono">{r.invoice_number||"—"}</td>
                <td>{fmt.date(r.invoice_date)}</td>
                <td className="text-right mono">{fmt.money(r.total_amount)}</td>
                <td className="text-right mono">{fmt.money(r.approved_amount)}</td>
                <td>{fmt.date(r.payment_date)}</td>
                <td>{r.payment_method||"—"}</td>
                <td className="mono">{r.check_number||"—"}</td>
                <td><StatusPill status={r.status} /></td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
  );

  const scaffolded = accessible && ["loss-run", "reimbursement-status", "open-closed",
    "adjuster-wip", "claim-payments", "advanced-claim-search"].includes(activeReport);
  return (
    <div className="command-center no-preview" style={{ height: "100%" }}>
      <aside className="cc-rail">
        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--navy)", marginBottom: 10 }}>Reports</div>
        {CATEGORY_ORDER.map((cat) => {
          const list = (grouped[cat] || []).filter((r) => reportVisible(r.id));
          if (!list.length) return null;
          return (
            <div key={cat} style={{ marginBottom: 10 }}>
              <div className="cc-rail-label">{cat}</div>
              {list.map((r) => (
                <button key={r.id} className={"cc-rail-item" + (r.id === activeReport ? " active" : "")}
                        onClick={() => switchReport(r.id)} title={r.description}>{r.label}</button>
              ))}
            </div>
          );
        })}
      </aside>
      {/* PAYMENT-HISTORY-REDESIGN C7 — "smart sticky": .scroll-away makes the PAGE
          the scroll container, so the filters/KPI band scrolls up out of view and
          the table's sticky thead pins to the top (large-font users see rows, not
          chrome). Other list-view pages keep the fixed-header contract. */}
      <div className="page list-view scroll-away">
        <div className="list-header">
          {/* REPORTS-UX-1 header strip: active report title + description + Saved Views */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 12, flexWrap: "wrap" }}>
            <div style={{ minWidth: 0 }}>
              <h2 style={{ margin: 0, fontSize: 18, color: "var(--navy)" }}>{activeReg.label || "Reports"}</h2>
              {activeReg.description && <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 3 }}>{activeReg.description}</div>}
            </div>
            {accessible && activeReg.savesViews && (
              <SavedViewBar views={savedViews}
                onApply={(v) => navigate({ page: "reports", params: { report: activeReport, view: v.params?.view, label: v.label } })}
                onSave={(label) => saveView(label, { report: activeReport, view: viewState })}
                onDelete={removeView} />
            )}
          </div>
        </div>
        {scaffolded ? (
          <ReportScaffold filters={rsFilters} actions={rsActions} kpis={rsKpis}>
            {resultsPanel}
          </ReportScaffold>
        ) : resultsPanel}
      </div>
    </div>
  );
}

// REPORTS-UX-1: Saved Views control in the workspace header. Per-user named
// snapshots of the current report view; Apply navigates to the saved view,
// Save names the current view, ✕ deletes.
function SavedViewBar({ views, onApply, onSave, onDelete }) {
  const [open, setOpen] = useState(false);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  const ref = React.useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setNaming(false); } };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const submit = () => { const n = name.trim(); if (!n) return; onSave(n); setName(""); setNaming(false); setOpen(false); };
  const pill = { border: "1px solid var(--border)", background: "#fff", borderRadius: 6, padding: "5px 12px", fontSize: 12, color: "var(--text2)", cursor: "pointer", whiteSpace: "nowrap" };
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button style={pill} onClick={() => setOpen((o) => !o)}>★ Saved Views{views.length ? ` (${views.length})` : ""} ▾</button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", right: 0, minWidth: 250, background: "#fff",
          border: "1px solid var(--border)", borderRadius: 8, boxShadow: "0 6px 20px rgba(0,0,0,.12)", zIndex: 50, padding: 6 }}>
          {views.length === 0 && <div style={{ padding: "8px 10px", fontSize: 12, color: "var(--text3)" }}>No saved views yet.</div>}
          {views.map((v) => (
            <div key={v.id} style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <button onClick={() => { onApply(v); setOpen(false); }}
                style={{ flex: 1, textAlign: "left", padding: "7px 10px", fontSize: 13, background: "none", border: "none", cursor: "pointer", color: "var(--text)", borderRadius: 6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v.label}</button>
              {/* WS0-B H22: was a single icon click with an optimistic local removal and no
                  confirm — a saved view is a hard DELETE with no restore path. */}
              <button title="Delete view" onClick={() => { if (window.confirm(`Delete the saved view "${v.label}"?\n\nThis can't be undone.`)) onDelete(v.id); }}
                style={{ border: "none", background: "none", color: "var(--text3)", cursor: "pointer", fontSize: 14, padding: "0 8px" }}>✕</button>
            </div>
          ))}
          <div style={{ borderTop: "1px solid var(--border)", marginTop: 6, paddingTop: 6 }}>
            {naming ? (
              <div style={{ display: "flex", gap: 4, padding: "0 4px" }}>
                <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="View name…"
                  onKeyDown={(e) => { if (e.key === "Enter") submit(); if (e.key === "Escape") setNaming(false); }}
                  style={{ flex: 1, padding: "6px 8px", border: "1px solid var(--border)", borderRadius: 4, fontSize: 12 }} />
                <button onClick={submit} className="btn btn-blue" style={{ padding: "4px 10px", fontSize: 12 }}>Save</button>
              </div>
            ) : (
              <button onClick={() => setNaming(true)} style={{ width: "100%", textAlign: "left", padding: "7px 10px", fontSize: 13, background: "none", border: "none", cursor: "pointer", color: "var(--blue)", fontWeight: 600 }}>+ Save current view…</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// REPORTS-UX-1: shown when a restored pin/saved-view points at a report the user
// can no longer access (capability removed). Discovery=access stays intact — the
// rail already omits forbidden reports; this only handles a stale deep link.
function UnavailablePanel({ reportLabel, onRemovePin }) {
  return (
    <div className="empty-state" style={{ padding: 32, textAlign: "center" }}>
      <div style={{ fontSize: 15, fontWeight: 600, color: "var(--navy)", marginBottom: 6 }}>Report unavailable</div>
      <div style={{ fontSize: 13, color: "var(--text2)", marginBottom: 16 }}>
        You don’t have access to {reportLabel ? `“${reportLabel}”` : "this report"} anymore. A pin or saved view may be out of date.
      </div>
      <button className="btn" onClick={onRemovePin}>Back to Home</button>
    </div>
  );
}
